/* eslint-disable react-hooks/exhaustive-deps, react-hooks/rules-of-hooks, react-refresh/only-export-components, no-empty */

// Simplified Buffer check - polyfill is handled at build level

import { COPY } from '../../content/copy';
import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState, useCallback } from "react";
import { loadFigletFont, generateAsciiArt, renderAsciiArtWithBitmapFont } from '../../utils/asciiArt';
import { DopeTextRenderer, bitmapFont, plasmaGlyphCache } from '../../engine/renderer/bitmapFont';
import { renderBlobBackground } from '../../engine/renderer/plasma';
import { getCanonicalSafeArea, drawSafeAreaDebug } from '../../engine/safeArea';
import { interactiveRegistry, drawDebugHitboxes, drawInteractiveRect } from '../../engine/interactivity';
import { logger } from '../../utils/logger.js';
import { JsonRpcProvider } from 'ethers';
// import { useConnectModal } from '@rainbow-me/rainbowkit'; // Moved to AuthContext
import { registerView, getView } from '../../engine/viewRegistry';
// Self-registering view modules
import '../../engine/views/login';
import '../../engine/views/confirmation';
import '../../engine/views/chat';
import { resetChatState, seedChatIntro, handleChatKey } from '../../engine/views/chat';
import { pickCRTProfile, CRT_PROFILES, resetCRTProfileCache } from '../../engine/crt/crtProfiles.js';
import '../../engine/views/promo_logo';
import '../../engine/views/icon_dcw';
import '../../engine/views/hero_universal';
import '../../engine/views/widget_splash';
import '../../engine/views/about';
import '../../engine/views/leaderboard';
import '../../engine/views/game_console';
import '../../engine/views/base_logo';
import '../../engine/views/main_menu';
import '../../engine/views/dopeos_home';
import '../../engine/views/street_home';
import '../../engine/views/comms_window';
import '../../engine/views/living_chat';
import { handleLivingChatKey } from '../../engine/views/living_chat';
import '../../engine/views/terminal_window';
import '../../engine/views/ledger_view';
import '../../engine/views/coat_view';
import '../../engine/views/apps_view';
import '../../engine/views/hronir_view';
import '../../engine/views/economy_dashboard';
import '../../engine/views/market_view';
import '../../engine/views/route_view'; import '../../engine/views/tickets_view';
import '../../engine/views/signals_view';
import '../../engine/views/journal_view';
import '../../engine/views/evidence_view'; import '../../engine/views/story_view';
import '../../engine/views/audit_view';
import '../../engine/views/purge_view';
import '../../engine/views/archive_view';
// Demo views for testing UI components
import '../../engine/views/ui_system_demo';  // DopeOS UI System demo
import { listViews } from '../../engine/viewRegistry';

// DEBUG: Log registered views
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const views = listViews();
    console.log('[DopeOS] Registered views:', views);
  }, 100);
}

import { getApiBase, getApiKey, getAuthWallet, clearAuth, invalidateAuth, setAuthWallet, setApiKey } from '../../utils/api';
import { isAuthenticated, signOut as authSignOut, getNonce } from '../../engine/auth/authService';
import { detectCapabilities, hasSponsoredCalls } from '../../utils/capabilities';
// PROFESSIONAL AUTHENTICATION SYSTEM - Using AuthContext with authFacade
import { useAuth } from '../../engine/auth/AuthContext.jsx';
import { isMobileDevice, PLATFORM, detectPlatform } from '../../engine/device.js';
import useGameState from '../../engine/gameState.js';
import { ensureCoreSession } from '../../stores/coreSession.js';
// authenticateUser removed - using frameBridge.login() via AuthContext
import getBaseMiniKitClient from '../../engine/wallet/baseMiniKit.js';

/**
 * DopeOS: Canonical buffer/logic for Dope Chain Wars terminal
 * Pure offscreen canvas buffer, never rendered in DOM
 * Now uses fixed CRT size (960x720) for Shadertoy fidelity
 *
 * ## API
 * - setView(viewName: string, params?: object): Switches the current view.
 *   - 'boot': Boot sequence (default)
 *   - 'logo': Shows the DOPE CHAIN WARS logo
 *   - 'text': Shows custom ASCII/text lines (params: { lines: string[] })
 *   - (extend as needed)
 *
 * Example:
 *   dopeOSRef.current.setView('logo');
 *   dopeOSRef.current.setView('text', { lines: ["Hello World"] });
 *
 * ## Rendering pipeline (pixel-perfect DOS/CRT emulation)
 * 1. All text/art is rendered to a low-res offscreen buffer (BUFFER_WIDTH x BUFFER_HEIGHT, e.g. 640x400, 16px font).
 * 2. The buffer is scaled up to the CRT canvas (CRT_WIDTH x CRT_HEIGHT) using drawImage with imageSmoothingEnabled=false.
 * 3. CRTShader uses the CRT canvas as texture.
 *
 * This ensures pixel-perfect, sharp, retro output regardless of CRT size.
 */
// Initial constants for module-level code (views registration). They may be overridden inside component for live rendering.
const __initProfile = pickCRTProfile();
const CRT_WIDTH = __initProfile.frame.w;
const CRT_HEIGHT = __initProfile.frame.h;
// Note: BUFFER_WIDTH/HEIGHT are now reactive inside DopeOS component
// These are kept for backwards compatibility but should not be used for rendering

// keep latest profile for debug overlay
const profileRef = { current: __initProfile };

// Canonical ASCII art for 'DOPE CHAIN WARS' (abecedary style, only the logo)
const DOPE_CHAIN_WARS_LOGO = [
  " █████╗  ██████╗ ██████╗ ██████╗ ███████╗",
  "██╔══██╗██╔══██╗██╔════╝ ██╔══██╗██╔════╝",
  "███████║██████╔╝██║      ██║  ██║█████╗  ",
  "██╔══██║██╔══██╗██║      ██║  ██║██╔══╝  ",
  "██║  ██║██████╔╝╚██████╗ ██████╔╝███████╗",
  "╚═╝  ╚═╝╚═════╝  ╚═════╝ ╚═════╝ ╚══════╝",
  "                                         ",
  " ██████╗ ██████╗  █████╗ ██╗███╗   ██╗    ",
  "██╔════╝ ██╔══██╗██╔══██╗██║████╗  ██║    ",
  "██║  ███╗██████╔╝███████║██║██╔██╗ ██║    ",
  "██║   ██║██╔══██╗██╔══██║██║██║╚██╗██║    ",
  "╚██████╔╝██║  ██║██║  ██║██║██║ ╚████║    ",
  " ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚═╝╚═╝  ╚═══╝    "
];

// Canonical boot script (mirrors src/engine/views/boot.js)
const BOOT_LINES = [
  '640K RAM SYSTEM 38911 BASIC BYTES FREE',
  'READY.',
  '',
  'INIT BIG BOSS AI . . .',
  'SYNC FARCASTER NETWORK',
  'CONNECTING BASE L2',
  'LINKING WORLD ID',
  'LOADING BOROUGH DATA',
  'SPAWNING LOAN SHARKS',
  'CHECKING ECONOMY BALANCE',
  '',
  'DOPE CHAIN WARS v1.0',
  'WELCOME, OPERATOR!',
];
const IS_EMBED = typeof window !== 'undefined' && window.top !== window;
// Must stay in sync with BOOT_LINE_DELAY in boot.js (0.35 s per line)
const BOOT_LINE_DELAY = 0.35; // sec / line – identical on desktop & embed to avoid drift
const BOOT_TOTAL = BOOT_LINE_DELAY * BOOT_LINES.length;
const ASCII_ART_DELAY = IS_EMBED ? 6 : 20; // shorter art display in embeds
// Duration (ms) to show the temporary "[ U N B R A N D E D ]" logo between boot and login
const UNBRANDED_LOGO_DELAY = 4000; // 4 seconds

// --- Blob animation tweakables (feel free to modify) ---
const BLOB_CELL_SIZE = 8;          // virtual grid cell size in pixels
const BLOB_SCALE = 0.5;            // off-screen render buffer scale (0 < n ≤ 1)
const BLOB_DENSITY = ' .,:;i1tfLCG0#?8@<>'; // revised ramp for smoother dithering (light → dark)
const BLOB_DITHER = 0.12;          // random noise added to value before lookup (0 = none)

// --- API configuration (front-end ↔ FastAPI) ---
const API_BASE_URL = getApiBase();

// --- Confirmation text sizing ---
const CONFIRM_TEXT_SCALE = 2.5;

// 1. Add Perfect DOS VGA 437 font-face for canvas and CSS usage (bitmap DOS font, open source)
// Legacy font-face removed – bitmap renderer is canonical.

// 2. Canonical font settings (adjustable via debugger)
// Font size now scales dynamically with buffer height so that profiles with
// very large buffers (e.g. `profile=full`) don't render microscopic text.
let CANON_FONT_SIZE = 16;           // will be overwritten in-component
let CANON_FONT_WEIGHT = 'normal';
let CANON_FONT_FAMILY = 'monospace';
let CANON_GLOW = 2;                // also scales with font size
let CANON_OFFSET_X = 0;
let CANON_OFFSET_Y = 0;

function updateCanonicalFont(bufferHeight) {
  // Use consistent baseline font size regardless of buffer size
  // This prevents UI scaling mismatches between different profiles
  CANON_FONT_SIZE = 16;
  CANON_GLOW = 2;
}

function renderAsciiArtCentered(ctx, art, width, height, fontSize, offsetX, offsetY, glow) {
  ctx.save();
  ctx.font = `normal ${fontSize}px ${CANON_FONT_FAMILY}`;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#39FF14';
  ctx.shadowColor = '#39FF14';
  ctx.shadowBlur = glow;
  const lineHeight = fontSize * 1.2;
  const artHeight = art.length * lineHeight;
  const startY = Math.floor((height - artHeight) / 2) + offsetY;
  const maxLen = Math.max(...art.map(line => ctx.measureText(line).width));
  const startX = Math.floor((width - maxLen) / 2) + offsetX;
  for (let i = 0; i < art.length; i++) {
    ctx.fillText(art[i], startX, startY + i * lineHeight);
  }
  ctx.restore();
}

function renderTextLines(ctx, lines, width, height, fontSize, offsetX, offsetY, glow, time) {
  ctx.save();
  ctx.font = `${CANON_FONT_WEIGHT} ${fontSize}px ${CANON_FONT_FAMILY}`;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#39FF14';
  ctx.shadowColor = '#39FF14';
  ctx.shadowBlur = glow;
  const lineHeight = fontSize * 1.2;
  const artHeight = lines.length * lineHeight;
  const startY = Math.floor((height - artHeight) / 2) + offsetY;
  // Find max width in px
  const maxLen = Math.max(...lines.map(line => ctx.measureText(line).width));
  const startX = Math.floor((width - maxLen) / 2) + offsetX;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], startX, startY + i * lineHeight);
  }
  ctx.restore();
}

// --- Feature toggles for experimental visuals ---
const USE_ORIGINAL_PLASMA = true;   // set false to fall back to canonical blob
const USE_WOBBLY_BOOT = true;   // subtle 80s terminal wobble

function computeOriginalPlasma(x, y, t) {
  // Ported from Shadertoy plasma formula (see design doc)
  let color = 0;
  color += 0.7 * Math.sin(0.5 * x + t / 5);
  color += 3.0 * Math.sin(1.6 * y + t / 5);
  color += 1.0 * Math.sin(10.0 * (y * Math.sin(t / 2) + x * Math.cos(t / 5)) + t / 2);
  const cx = x + 0.5 * Math.sin(t / 2);
  const cy = y + 0.5 * Math.cos(t / 4);
  color += 0.4 * Math.sin(Math.sqrt(100.0 * cx * cx + 100.0 * cy * cy + 1.0) + t);
  color += 0.9 * Math.sin(Math.sqrt(75.0 * cx * cx + 25.0 * cy * cy + 1.0) + t);
  color += -1.4 * Math.sin(Math.sqrt(256.0 * cx * cx + 25.0 * cy * cy + 1.0) + t);
  color += 0.3 * Math.sin(0.5 * y + x + Math.sin(t));
  return 0.5 + 0.499 * Math.sin(color); // normalise 0..1
}

// ----------------- View registration (legacy views, will coexist with modular ones) -----------------

registerView('logo', (ctx, t, w, h) => {
  renderAsciiArtCentered(
    ctx,
    DOPE_CHAIN_WARS_LOGO,
    w,
    h,
    CANON_FONT_SIZE,
    CANON_OFFSET_X,
    CANON_OFFSET_Y,
    CANON_GLOW
  );
});

registerView('text', (ctx, t, w, h, p = {}) => {
  renderAsciiArtCentered(
    ctx,
    p.lines ? p.lines : [""],
    w,
    h,
    CANON_FONT_SIZE,
    CANON_OFFSET_X,
    CANON_OFFSET_Y,
    CANON_GLOW
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// 404 – "Not Found" view (minimal, CRT-safe)
// Renders a large ASCII ‟404" using the canonical ANSI Shadow font and a single
// line of DOS-style text below.  Fits safely within the canonical safe-area on
// all profiles.
// ─────────────────────────────────────────────────────────────────────────────
registerView('404', (ctx, t, w, h, params = {}) => {
  // Fallback while fonts load
  if (!DopeTextRenderer.isReady()) {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    ctx.font = 'bold 32px monospace';
    ctx.fillStyle = '#39FF14';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('404 – Page not found', w / 2, h / 2);
    return;
  }

  const buffer = { w, h };
  const safe = getCanonicalSafeArea(buffer);

  // Clear background
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);

  // ─── ASCII "404" ────────────────────────────────────────────────
  const asciiStr = '404';
  const asciiMetrics = DopeTextRenderer.measureASCIIArt(asciiStr);
  const maxAsciiWidth = safe.width * 0.9; // 10 % horizontal margin
  let asciiScale = 1;
  if (asciiMetrics.width > 0 && asciiMetrics.width > maxAsciiWidth) {
    asciiScale = maxAsciiWidth / asciiMetrics.width;
  }
  const asciiW = asciiMetrics.width * asciiScale;
  const asciiH = asciiMetrics.height * asciiScale;
  const asciiX = safe.left + (safe.width - asciiW) / 2;
  const asciiY = safe.top + (safe.height - asciiH) / 2 - 24; // shift up a bit

  DopeTextRenderer.renderASCIIArt(ctx, asciiStr, asciiX, asciiY, asciiScale);

  // ─── Caption text ────────────────────────────────────────────────
  const caption = params.message || 'PAGE NOT FOUND';
  const textScale = 1.6;
  const textMetrics = DopeTextRenderer.measureDOSText(caption, textScale);
  const textX = safe.left + (safe.width - textMetrics.width) / 2;
  const textY = asciiY + asciiH + 16; // small gap below ASCII art
  DopeTextRenderer.renderDOSText(ctx, caption, textX, textY, textScale);
});

// ─────────────────────────────────────────────────────────────────────────────
// 500 – "Server Error" view
// Mirrors the 404 layout so both fit within the safe-area across profiles.
// ─────────────────────────────────────────────────────────────────────────────
registerView('500', (ctx, t, w, h, params = {}) => {
  if (!DopeTextRenderer.isReady()) {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    ctx.font = 'bold 32px monospace';
    ctx.fillStyle = '#39FF14';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('500 – Server error', w / 2, h / 2);
    return;
  }

  const buffer = { w, h };
  const safe = getCanonicalSafeArea(buffer);

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);

  const asciiStr = '500';
  const asciiMetrics = DopeTextRenderer.measureASCIIArt(asciiStr);
  const maxAsciiWidth = safe.width * 0.9;
  let asciiScale = 1;
  if (asciiMetrics.width > 0 && asciiMetrics.width > maxAsciiWidth) {
    asciiScale = maxAsciiWidth / asciiMetrics.width;
  }
  const asciiW = asciiMetrics.width * asciiScale;
  const asciiH = asciiMetrics.height * asciiScale;
  const asciiX = safe.left + (safe.width - asciiW) / 2;
  const asciiY = safe.top + (safe.height - asciiH) / 2 - 24;

  DopeTextRenderer.renderASCIIArt(ctx, asciiStr, asciiX, asciiY, asciiScale);

  const caption = params.message || 'SERVER ERROR';
  const textScale = 1.6;
  const textMetrics = DopeTextRenderer.measureDOSText(caption, textScale);
  const textX = safe.left + (safe.width - textMetrics.width) / 2;
  const textY = asciiY + asciiH + 16;
  DopeTextRenderer.renderDOSText(ctx, caption, textX, textY, textScale);
});

// ─────────────────────────────────────────────────────────────────────────────
// Generic error view – renders dynamic ASCII code + caption
// Params: { code: string|number, message?: string }
// ─────────────────────────────────────────────────────────────────────────────
registerView('error', (ctx, t, w, h, params = {}) => {
  if (!DopeTextRenderer.isReady()) {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    ctx.font = 'bold 32px monospace';
    ctx.fillStyle = '#39FF14';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Loading…', w / 2, h / 2);
    return;
  }

  const codeStr = String(params.code || 'ERR');
  const caption = params.message || (codeStr.startsWith('4')
    ? 'PAGE NOT FOUND'
    : codeStr.startsWith('5')
      ? 'SERVER ERROR'
      : 'ERROR');

  const buffer = { w, h };
  const safe = getCanonicalSafeArea(buffer);

  // Background
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);

  // ASCII error code – scale to 90 % of safe width
  const asciiMetrics = DopeTextRenderer.measureASCIIArt(codeStr);
  const maxAsciiWidth = safe.width * 0.9;
  let asciiScale = 1;
  if (asciiMetrics.width > 0 && asciiMetrics.width > maxAsciiWidth) {
    asciiScale = maxAsciiWidth / asciiMetrics.width;
  }
  const asciiW = asciiMetrics.width * asciiScale;
  const asciiH = asciiMetrics.height * asciiScale;
  const asciiX = safe.left + (safe.width - asciiW) / 2;
  const asciiY = safe.top + (safe.height - asciiH) / 2 - 24;

  DopeTextRenderer.renderASCIIArt(ctx, codeStr, asciiX, asciiY, asciiScale);

  // Caption below ASCII code
  const textScale = 1.6;
  const textMetrics = DopeTextRenderer.measureDOSText(caption, textScale);
  const textX = safe.left + (safe.width - textMetrics.width) / 2;
  const textY = asciiY + asciiH + 16;
  DopeTextRenderer.renderDOSText(ctx, caption, textX, textY, textScale);
});

// Aliases for 404 / 500 pointing to generic renderer (overrides previous regs)
registerView('404', (ctx, t, w, h, params = {}) => {
  getView('error')(ctx, t, w, h, { code: '404', message: params.message || 'PAGE NOT FOUND' });
});

registerView('500', (ctx, t, w, h, params = {}) => {
  getView('error')(ctx, t, w, h, { code: '500', message: params.message || 'SERVER ERROR' });
});

// ─────────────────────────────────────────────────────────────────────────────
// LOADING view – shown during wallet authentication / slow network hops
// ─────────────────────────────────────────────────────────────────────────────
registerView('loading', (ctx, t, w, h) => {
  // Black background
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);

  // Production debug: track loading view state
  if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
    if (!window.DCW_LOADING_STATE_LOGGED) {
      console.log(`[Loading] renderLoading called - DopeTextRenderer ready: ${DopeTextRenderer.isReady()}`);
      window.DCW_LOADING_STATE_LOGGED = true;
    }
  }

  // FIXED: Always render loading text even if DopeTextRenderer not ready (fallback to canvas text)
  const buffer = { w, h };
  const safe = getCanonicalSafeArea(buffer);
  const msg = 'CONNECTING';
  const dots = '.'.repeat(Math.floor(t % 3) + 1);
  const fullMsg = `${msg} ${dots}`;

  if (!DopeTextRenderer.isReady()) {
    ctx.save();
    ctx.font = 'bold 20px monospace';
    ctx.fillStyle = '#39FF14';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(fullMsg, w / 2, h / 2);
    ctx.restore();

    // Production debug: confirm fallback rendering
    if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
      if (!window.DCW_LOADING_FALLBACK_LOGGED) {
        console.log('[Loading]  Rendering fallback loading text (DopeTextRenderer not ready)');
        window.DCW_LOADING_FALLBACK_LOGGED = true;
      }
    }
    return;
  }

  // DopeTextRenderer is ready - use high-quality bitmap font rendering
  // Dynamic scale so the word fits 80% of safe width on any profile.
  let scale = 1.0;
  let metrics = DopeTextRenderer.measureDOSText(fullMsg, scale);
  if (metrics.width > 0) {
    const targetW = safe.width * 0.8;
    scale = Math.max(1.2, Math.min(6, targetW / metrics.width));
    metrics = DopeTextRenderer.measureDOSText(fullMsg, scale);
  }
  const x = safe.left + (safe.width - metrics.width) / 2;
  const y = safe.top + (safe.height - metrics.height) / 2;
  DopeTextRenderer.renderDOSText(ctx, fullMsg, x, y, scale);

  // Production debug: confirm bitmap font rendering
  if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
    if (!window.DCW_LOADING_BITMAP_LOGGED) {
      console.log('[Loading]  Rendering bitmap font loading text');
      window.DCW_LOADING_BITMAP_LOGGED = true;
    }
  }
});


function renderView(ctx, time, view, params, width, height) {
  // Always render the requested view directly into the buffer.
  ctx.clearRect(0, 0, width, height);
  const fn = getView(view);
  fn(ctx, time, width, height, params);
  // Optional debug overlay: visualize safe-area bounds (magenta translucent box)
  if (DEBUG_SAFE_AREA) {
    try {
      drawSafeAreaDebug(ctx, { w: width, h: height });
    } catch {/* ignore */ }
  }
}

// Load the FIGlet font once at app start
if (typeof window !== 'undefined' && !window._figletFontLoaded) {
  window._figletFontLoaded = true;
  loadFigletFont('ANSI Shadow', '/fonts/figlet/ANSI_Shadow.flf');
}

// Example: Render a dynamic ASCII art title in the buffer (call this where needed)
async function renderDynamicAsciiTitle(ctx, title, x, y, lineHeight = 32, color = '#39FF14') {
  const asciiArt = await generateAsciiArt(title, { font: 'ANSI Shadow', width: 60 });
  renderAsciiArtWithBitmapFont(ctx, asciiArt, x, y, lineHeight, color, bitmapFont);
}

const ASCII_TITLE = 'DOPE CHAIN WARS';
const ASCII_TITLE_FONT = 'ANSI Shadow';
const ASCII_TITLE_WIDTH = 60;
const ASCII_TITLE_LINE_HEIGHT = 28;
const ASCII_TITLE_COLOR = '#39FF14';
const ASCII_TITLE_TIMEOUT = 10; // seconds before blob screensaver

// --- Session storage key to avoid reload loops (Base miniKit) ---
const reloadFlagKey = 'dcw_reload_pending';
function detectMobile() {
  // Use direct device characteristics, not profile-based detection
  // This matches the same logic used in login.js, crtProfiles.js, and device.js
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;

  const ua = navigator.userAgent || '';
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
    ('ontouchstart' in window) ||
    (navigator.maxTouchPoints || 0) > 0;
}

// --- ASCII art cache (in-memory, per session) ---
const asciiArtCache = {};

const BUFFER_Y_OFFSET = 0;

const SAFE_X = 0;
const SAFE_Y = 0;
const SAFE_W = 960;
const SAFE_H = 720;

// Utility to measure bitmap text width (monospaced)
function measureBitmapTextWidth(text, font) {
  const { cellWidth } = font.meta;
  return text.length * cellWidth;
}

// Helper: parse query params once
const urlParams = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search) : new URLSearchParams();
// Debug flag (safe-area overlay) – can be set via env var or ?safearea
const DEBUG_SAFE_AREA = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SAFE_AREA_DEBUG === 'true') || urlParams.has('safearea');
const SHOW_HITBOXES = urlParams.has('hitbox');
const FORCE_BOOT = urlParams.has('reset');
const PROMO_MODE = (typeof window !== 'undefined') && (urlParams.has('promo') || window.location.pathname.includes('promo'));

// Helper: detect a previously registered wallet (stored after first signup)
function getPersistedWallet() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('dcw_waitlist_wallet');
  } catch {
    return null;
  }
}

function setPersistedWallet(addr) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem('dcw_waitlist_wallet', addr);
  } catch { }
}

// Helper: boot sequence cookie - show boot only once per browser/mini-app
const BOOT_COOKIE_KEY = 'dcw_boot_sequence_seen';

function hasSeenBootSequence() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(BOOT_COOKIE_KEY) === '1';
  } catch {
    return false;
  }
}

function setBootSequenceSeen() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(BOOT_COOKIE_KEY, '1');
  } catch { /* ignore */ }
}

//  AUTOMATIC CONNECTED WALLET DETECTION
// Function that automatically detects already connected wallets on load
async function detectConnectedWallet() {
  if (typeof window === 'undefined' || !window.ethereum) {
    console.log('[DopeOS] No Ethereum provider detected');
    return null;
  }

  try {
    console.log('[DopeOS] Checking for connected wallet...');

    // Check if there are already connected accounts
    const accounts = await window.ethereum.request({ method: 'eth_accounts' });

    if (accounts && accounts.length > 0) {
      const address = accounts[0];
      console.log('[DopeOS] Found connected wallet:', address);

      // Check if we already have a valid session for this wallet
      const apiKey = getApiKey();
      const storedWallet = getAuthWallet();

      if (apiKey && storedWallet === address) {
        console.log('[DopeOS] Existing valid session found');
        return { address, hasValidSession: true };
      }

      // Attempt automatic login with the connected wallet
      console.log('[DopeOS] Attempting automatic login with connected wallet...');
      return { address, hasValidSession: false, needsAuth: true };
    }

    console.log('[DopeOS] No connected wallet found');
    return null;
  } catch (error) {
    console.error('[DopeOS] Error detecting connected wallet:', error);
    return null;
  }
}

// Listener to react to wallet account switches (optional for future UI)
function addWalletListeners(onChange) {
  if (typeof window === 'undefined') return () => { };
  const handlers = [];
  const ethArr = [];
  if (window.ethereum) ethArr.push(window.ethereum);
  if (window.farcaster?.ethereum) ethArr.push(window.farcaster.ethereum);
  ethArr.forEach((prov) => {
    const handler = (accounts) => onChange(accounts);
    prov.on?.('accountsChanged', handler);
    handlers.push([prov, handler]);
  });
  return () => handlers.forEach(([p, h]) => p.removeListener?.('accountsChanged', h));
}

// Single, shared provider with explicit network to skip automatic detection
const ENS_PROVIDER = new JsonRpcProvider('https://cloudflare-eth.com', {
  name: 'homestead',
  chainId: 1,
});

// Add this function after the notify function
function checkSession() {
  // Get API key and wallet from localStorage directly
  const apiKey = localStorage.getItem('dcw_api_key') || localStorage.getItem('apiKey');
  const wallet = localStorage.getItem('dcw_wallet');

  console.log('[DopeOS] Checking session status:', {
    hasApiKey: !!apiKey,
    hasWallet: !!wallet
  });

  // If we have both an API key and wallet, consider the session valid
  if (apiKey && wallet) {
    return true;
  }

  return false;
}

const DopeOS = forwardRef(function DopeOS({ profile, style, onStateChange }, ref) {
  const selectedCharacter = useGameState((state) => state.selectedCharacter);
  const setSelectedCharacter = useGameState((state) => state.setSelectedCharacter);
  const introCompleted = useGameState((state) => state.introCompleted);
  const setIntroCompleted = useGameState((state) => state.setIntroCompleted);
  // Activate profile lock during boot/login for stability
  if (typeof globalThis !== 'undefined') {
    globalThis.__DCW_PROFILE_LOCK = (profile === undefined); // true during initial boot
  }

  // Define sendVirtualsAction inside component using useCallback for stability
  const sendVirtualsAction = React.useCallback((action) => {
    const v = globalThis.virtuals ?? (typeof window !== 'undefined' ? window.virtuals : undefined) ?? {};
    const payload = { ...action, ts: Date.now() };
    try {
      v.requestTurn?.([payload]);
    } catch (err) {
      console.warn('[Virtuals] requestTurn failed:', err);
    }
  }, []);

  // Add mountRef for DOM mounting point
  const mountRef = useRef(null);

  // PROFESSIONAL AUTH: Use AuthContext instead of polling
  // This provides reactive authentication state with zero polling overhead
  const { isAuthenticated: authIsAuthenticated, wallet: authWallet, loading: authLoading, login: authLogin, logout: authLogout, error: authError, nativeUser: authNativeUser } = useAuth();
  const notify = React.useMemo(() => {
    return (message, type = 'info') => {
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        try {
          window.dispatchEvent(
            new CustomEvent('dcw-notify', {
              detail: { message, type, timestamp: Date.now() },
            }),
          );
        } catch (dispatchErr) {
          console.warn('[DopeOS] Failed to dispatch dcw-notify event', dispatchErr);
        }
      }

      if (typeof console !== 'undefined') {
        const logMessage = `[DopeOS notify] ${message}`;
        if (type === 'error') {
          console.error(logMessage);
        } else if (type === 'warn' || type === 'warning') {
          console.warn(logMessage);
        } else {
          console.log(logMessage);
        }
      }
    };
  }, []);
  const notifyPortal = notify;

  // Check URL parameters for specific view requests (for image generation)
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  // Detect explicit ?view param first; otherwise, if the current pathname is
  // *not* the root "/", we assume the SPA fallback served index.html for an
  // unknown route and we force the 404 view in local dev as well as in any
  // mis-configured envs that omit the nginx 302.
  let requestedView = urlParams.get('view');
  if (!requestedView && typeof window !== 'undefined') {
    const path = window.location.pathname || '/';
    if (path === '/promo' || path === '/promo/') {
      requestedView = 'promo_logo';
    } else if (path === '/base-logo' || path === '/base_logo' || path === '/base-logo/' || path === '/base_logo/') {
      requestedView = 'base_logo';
    } else if (path !== '/' && path !== '/index.html') {
      requestedView = '404';
    }
  }
  if (requestedView && typeof window !== 'undefined' && window.__DCW_REQUESTED_VIEW_CLEARED) {
    requestedView = null;
  }
  const NUMERIC_VIEW_REGEX = /^\d+$/;
  const isNumericView = NUMERIC_VIEW_REGEX.test(requestedView);
  const isImageGeneration = ['icon_dcw', 'hero_universal', 'widget_splash', 'promo_logo'].includes(requestedView) || isNumericView;

  // Use the profile passed from App.jsx (which already handles URL-based forcing)
  const forcedProfile = profile || pickCRTProfile();

  console.log(`[DopeOS] Using profile: ${forcedProfile.id}, buffer: ${forcedProfile.buffer.w}x${forcedProfile.buffer.h}, frame: ${forcedProfile.frame.w}x${forcedProfile.frame.h}`);

  // MOBILE DEBUG: Log DopeOS initialization
  if (typeof window !== 'undefined' && window.mobileLog) {
    window.mobileLog('🖥️ DopeOS init');
    window.mobileLog('Profile:', forcedProfile.id);
    window.mobileLog('Buffer:', `${forcedProfile.buffer.w}x${forcedProfile.buffer.h}`);
  }

  // ---------- dimensions from profile (reactive to profile changes) ----------
  const { frame: { w: FRAME_W, h: FRAME_H }, buffer: { w: BUFFER_WIDTH, h: BUFFER_HEIGHT } } = forcedProfile;

  // Dynamically adjust canonical font size ONLY when we are rendering the
  // literal fullscreen buffer profile (id === 'full'). Using it on other
  // profiles—especially `MOBILE_16_9`—was inflating text and throwing off
  // layout & hit-testing. For every other profile we revert to the canonical
  // baseline (16 px, glow 2).
  if (forcedProfile.id === 'full') {
    updateCanonicalFont(BUFFER_HEIGHT);
  } else {
    CANON_FONT_SIZE = 16;
    CANON_GLOW = 2;
  }

  // --- Canonical: always create a CRT canvas offscreen for CRTShader texture ---
  const canvasRef = useRef(null);
  if (!canvasRef.current && typeof window !== 'undefined') {
    const crtCanvas = document.createElement('canvas');
    let scaleCanvas = forcedProfile.bufferScale || 1;
    try {
    const isActualDesktop = (() => {
        if (typeof navigator === 'undefined' || typeof window === 'undefined') return true;
        const ua = navigator.userAgent || '';
        return !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) &&
          !('ontouchstart' in window) &&
          (navigator.maxTouchPoints || 0) === 0;
      })();

      // Keep full resolution for desktop devices regardless of SDK platform
      if (isActualDesktop && scaleCanvas < 1 && forcedProfile.id !== '9_16') {
        scaleCanvas = 1; // keep full-res only for actual desktop devices
      }
    } catch {/* SSR */ }
    CANON_FONT_SIZE = 16;
    CANON_GLOW = 2;
    crtCanvas.width = Math.round(FRAME_W * scaleCanvas);
    crtCanvas.height = Math.round(FRAME_H * scaleCanvas);
    canvasRef.current = crtCanvas;
  }

  // Update canvas dimensions when profile changes
  useEffect(() => {
    if (canvasRef.current) {
      let scaleCanvas = forcedProfile.bufferScale || 1;
      try {
        const isFarcasterDesktop = typeof window !== 'undefined' && window.farcaster && window.parent === window;
        // Allow buffer downscaling if current profile is portrait mobile (9_16)
        if (isFarcasterDesktop && scaleCanvas < 1 && forcedProfile.id !== '9_16') {
          scaleCanvas = 1; // keep full-res only for desktop-style profiles
        }
      } catch {/* SSR */ }
      CANON_FONT_SIZE = 16;
      CANON_GLOW = 2;
      canvasRef.current.width = Math.round(FRAME_W * scaleCanvas);
      canvasRef.current.height = Math.round(FRAME_H * scaleCanvas);
      console.log(`[DopeOS] Updated canvas dimensions to ${FRAME_W}x${FRAME_H} (profile: ${forcedProfile.id})`);
      
      // SCREENSHOT SUPPORT: Expose DopeOS canvas directly for headless screenshot capture
      // This bypasses CRTShader which may fail in headless Chrome (no WebGL)
      if (typeof window !== 'undefined') {
        window.DCW_getDopeOSCanvas = () => canvasRef.current;
        console.log('[DopeOS] Exposed DopeOS canvas via window.DCW_getDopeOSCanvas');
      }
    }
  }, [FRAME_W, FRAME_H, forcedProfile.id]);
  const bufferRef = useRef(null); // Offscreen buffer
  const animationRef = useRef(null);
  const signingRef = React.useRef(false);

  // --- State machine: 'boot' | 'asciiTitle' | 'waitlist' | 'blob' | 'chat' ---
  const persistedWallet = getPersistedWallet();
  // We optimistically remember the wallet address (for ENS lookup), but we do
  // NOT consider the session confirmed until the API key is validated.
  const [userWalletAddress, setUserWalletAddress] = useState(persistedWallet);

  // View & auth state – allow ?view=xyz to seed initial state for dev / screenshots
  // Boot sequence shows only once per browser/mini-app (cookie-based), unless ?reset is present
  const shouldSkipBoot = !FORCE_BOOT && hasSeenBootSequence();
  const initialView = requestedView || (shouldSkipBoot ? 'unbranded' : 'boot');
  const [osState, setOsStateRaw] = useState(initialView);
  const [isSigning, setIsSigning] = useState(false);
  
  const setOsState = useCallback((newState) => {
    setOsStateRaw(newState);
  }, []);

  // Release global profile lock once beyond boot/login to allow dynamic picks
  // AND force re-detection on mount to fix mobile app profile caching issues
  useEffect(() => {
    // 1. Break lock if we've moved past boot/login
    if (globalThis.__DCW_PROFILE_LOCK && osState !== 'boot' && osState !== 'login') {
      delete globalThis.__DCW_PROFILE_LOCK;
      resetCRTProfileCache();
    }

    // 2. WATCHDOG: Aggressively check for Mobile App environment mismatches every second
    // This catches the case where detection was "Desktop" on load, but SDK/UA now says "Mobile App"
    const profileWatchdog = setInterval(() => {
      const currentProfile = globalThis.__DCW_LAST_PROFILE;
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      const isMobileApp = /Farcaster|Base|World App|WorldCoin/i.test(ua);

      // If we are definitely in a mobile app, but running Desktop profile...
      if (isMobileApp && currentProfile && currentProfile.id === '4_3') {
        console.warn('[DopeOS] 🚨 PROFILE MISMATCH DETECTED (Watchdog) 🚨');
        console.warn('[DopeOS] Environment is Mobile App, but Profile is Desktop 4:3');
        console.warn('[DopeOS] Forcing immediate cache reset and re-render...');

        // Nuke everything
        delete globalThis.__DCW_PROFILE_LOCK;
        resetCRTProfileCache();

        // Force a state update to trigger re-render of CRTShader + DopeOS
        // We toggle a dummy state to force the update
        setViewState(prev => prev === 'refresh' ? 'refresh_done' : 'refresh');
      }
    }, 1000);

    return () => clearInterval(profileWatchdog);
  }, [osState]);
  const [view, setViewState] = useState('boot');

  // Force OS state to requestedView on mount to override auth redirects
  React.useEffect(() => {
    if (requestedView) {
      setOsState(requestedView);
    }
  }, []);
  const requestedViewDidMountRef = useRef(false);
  React.useEffect(() => {
    if (requestedViewDidMountRef.current && requestedView && osState !== requestedView) {
      window.__DCW_REQUESTED_VIEW_CLEARED = true;
    }
  }, [osState, requestedView]);
  React.useEffect(() => {
    requestedViewDidMountRef.current = true;
  }, []);
  const [viewParams, setViewParams] = useState({});
  const [waitlistConfirmed, setWaitlistConfirmed] = useState(false);
  const [userEnsName, setUserEnsName] = useState(null);
  const [authGracePeriod, setAuthGracePeriod] = useState(false);
  const [isMobile, setIsMobile] = useState(detectMobile());
  const [virtualsGameState, setVirtualsGameState] = useState({});

  useEffect(() => {
    function onResize() {
      // Only allow profile recalculation once we are beyond boot/login views
      if (osState === 'boot' || osState === 'login') return;
      setIsMobile(detectMobile());
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Wait for canonical font to be loaded before first render
  useEffect(() => {
    if (document.fonts) {
      document.fonts.ready.then(() => setFontReady(true));
    } else {
      setFontReady(true); // fallback for browsers without Font Loading API
    }
  }, []);

  // Only load canonical bitmap fonts (no figlet, no asciiArt dynamic logic)
  useEffect(() => {
    let cancelled = false;
    async function loadFonts() {
      logger.system('Initializing DopeOS with canonical text renderer...');
      await DopeTextRenderer.init();
      if (!cancelled) {
        setFontReady(true);
        console.log('DopeOS canonical text renderer ready');

        // Mathematical text rendering removed - using optimized bitmap fonts instead
      }
    }
    loadFonts();
    return () => { cancelled = true; };
  }, []);

  const [fontReady, setFontReady] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    detectCapabilities();
  }, []);

  // ─── Optional debugging ──────────────────────────────────────────────
  // Enable verbose logging via ?debugLogs=1 in the URL. This prevents the
  // development console from being flooded during normal usage.
  const SHOW_DEBUG_LOGS = (typeof window !== 'undefined') &&
    window.location && window.location.search.includes('debugLogs=1');

  useEffect(() => {
    if (SHOW_DEBUG_LOGS) {
      logger.debug(`osState changed → ${osState}`);
    }
  }, [osState]);

  // Keyboard handling for chat view - capture all keys including when canvas has focus
  useEffect(() => {
    function handleKeyDown(e) {
      const view = window.DCW_CURRENT_VIEW;
      const isChatView = view === 'chat' || view === 'comms_window';
      const isLivingChat = view === 'living_chat';
      if (!isChatView && !isLivingChat) return;

      // Let the invisible mobile soft-keyboard input own its own keystrokes.
      const target = e.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

      // Ignore key repeat events (when holding a key down)
      if (e.repeat) {
        e.preventDefault();
        return;
      }

      // Prevent default for chat keys to stop page scrolling etc
      if (e.key === 'Enter' || e.key === 'Backspace' || e.key.length === 1) {
        e.preventDefault();
      }

      if (isLivingChat) {
        if (handleLivingChatKey(e.key)) e.stopImmediatePropagation();
      } else {
        handleChatKey(e.key);
      }
    }

    // One capture listener is enough; registering on both window and document
    // delivered every keystroke twice and dropped fast repeated characters.
    window.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  // ----- Mobile swipe-back detection -----
  const pointerStartRef = useRef(null);    // {x, y}
  const osStateRef = useRef(osState);
  useEffect(() => { osStateRef.current = osState; }, [osState]);
  const onboardingPendingRef = useRef(false);
  useEffect(() => {
    if (osState === 'waitlistOnboarding' && onboardingPendingRef.current) {
      console.log('[DopeOS] Base onboarding pending – waiting for user to return from Coinbase Wallet');
    }
  }, [osState]);

  const SWIPE_THRESHOLD_PX = 60;   // more sensitive on mobile
  const MAX_VERTICAL_DRIFT = 40; // tighter vertical tolerance

  // Deduplication: ignore repeat click-like events within 300 ms on same element
  const lastTapRef = useRef({ time: 0, id: null });
  const lastGestureAtRef = useRef(0);
  const walletActionRef = useRef(0);

  useImperativeHandle(ref, () => {
    console.log('[DopeOS] useImperativeHandle: Creating ref interface');

    // CRITICAL FIX: Define handlePointerEvent as a stable function first
    const handlePointerEventFn = (e, mx, my) => {
      // Direct pass-through of pre-mapped coordinates from CRTShader
      // The coordinates coming from CRTShader's crtCanvasToBufferXYFromEvent
      // are already in the correct Buffer Space (640x400) and account for
      // barrel distortion, scaling, and letterboxing.
      const bufferMx = mx;
      const bufferMy = my;

      console.log(`[DopeOS] handlePointerEvent called: ${e.type} at ${mx},${my} (Buffer: ${Math.round(bufferMx)},${Math.round(bufferMy)})`);

      // --- Mobile swipe-right to go back (Chat / About) -------------------
      if (typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        if (e.type === 'pointerdown' || e.type === 'touchstart') {
          pointerStartRef.current = { x: mx, y: my };
        } else if (e.type === 'pointerup' || e.type === 'touchend') {
          if (pointerStartRef.current) {
            const dx = mx - pointerStartRef.current.x;
            const dy = my - pointerStartRef.current.y;
            if (dx > SWIPE_THRESHOLD_PX && Math.abs(dy) < MAX_VERTICAL_DRIFT) {
              // Trigger back only from chat or about views
              if (osStateRef.current === 'chat' || osStateRef.current === 'about') {
                setOsState('confirmation');
                pointerStartRef.current = null;
                // Do not continue – avoid accidental taps after swipe
                return;
              }
            }
          }
        }
      }

      // Determine which element(s) contain the pointer.  When multiple overlap,
      // choose the *smallest* by area so that tiny buttons like the "X" are not
      // hidden under large scrolling panes.
      console.log(`[DopeOS] Searching ${interactiveRegistry.elements.length} elements for hit at ${bufferMx},${bufferMy}`);
      let idx = -1;
      let smallestArea = Infinity;
      interactiveRegistry.elements.forEach((el, i) => {
        if (bufferMx >= el.x && bufferMx <= el.x + el.w && bufferMy >= el.y && bufferMy <= el.y + el.h) {
          const area = el.w * el.h;
          if (area < smallestArea) {
            smallestArea = area;
            idx = i;
          }
        }
      });
      interactiveRegistry.setHoverByIndex(idx);
      // Treat release events differently per platform to prevent duplicates.
      const isMobileEnv = typeof navigator !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|Opera Mini/i.test(navigator.userAgent);
      // CRITICAL FIX: Add 'click' AND 'touchstart' to CLICK_LIKE array
      // Desktop browsers fire 'click' events, not 'pointerup'
      // Mobile fires 'touchstart' BEFORE 'touchend' - need both
      // Without these, buttons were unclickable on their respective platforms
      const CLICK_LIKE = ['click', 'touchstart', 'pointerup', 'mouseup', 'touchend'];
      console.log(`[DopeOS] Processing event: ${e.type}, is click-like: ${CLICK_LIKE.includes(e.type)}, found element: ${idx !== -1}`);
      if (CLICK_LIKE.includes(e.type)) {
        console.log('[DopeOS] Interactive elements snapshot:', interactiveRegistry.elements.map((el, i) => ({
          index: i,
          id: el.id,
          x: Math.round(el.x),
          y: Math.round(el.y),
          w: Math.round(el.w),
          h: Math.round(el.h),
        })));
      }

      // MOBILE DEBUG: Log element search details
      if (typeof window !== 'undefined' && window.mobileLog && idx === -1 && CLICK_LIKE.includes(e.type)) {
        window.mobileLog(`❌ No element at ${Math.round(mx)},${Math.round(my)}`);
        window.mobileLog(`Elements: ${interactiveRegistry.elements.length}`);
        if (interactiveRegistry.elements.length > 0) {
          const el = interactiveRegistry.elements[0];
          window.mobileLog(`El0: ${el.id} [${el.x},${el.y} ${el.w}x${el.h}]`);
        }
      }

      if (CLICK_LIKE.includes(e.type) && idx !== -1) {
        const gestureNow = Date.now();
        if (gestureNow - lastGestureAtRef.current < 220) {
          return;
        }
        lastGestureAtRef.current = gestureNow;
        const el = interactiveRegistry.elements[idx];
        console.log(`[DopeOS] Element found:`, { id: el.id, x: el.x, y: el.y, w: el.w, h: el.h });

        // ─── Deduplicate rapid multi-fired events (mobile: down → start → end) ───
        const now = Date.now();
        const timeSinceLastTap = now - lastTapRef.current.time;
        const isSameElement = lastTapRef.current.id === el.id;

        // CRITICAL FIX: Reduced throttle from 500ms to 150ms
        // 500ms was too aggressive and blocked legitimate taps in Base/World apps
        // Only prevent SAME element tapped within 150ms (true double-tap)
        const isDuplicate = isSameElement && timeSinceLastTap < 150;

        if (isDuplicate) {
          console.log(`[DopeOS] Duplicate tap prevented: ${el.id}, delta: ${timeSinceLastTap}ms`);
          // MOBILE DEBUG
          if (typeof window !== 'undefined' && window.mobileLog) {
            window.mobileLog(`⏸️ Dup tap blocked: ${timeSinceLastTap}ms`);
          }
        } else {
          console.log(`[DopeOS] Tap accepted: ${el.id}, calling onClick`);
          // MOBILE DEBUG: Show tap was accepted
          if (typeof window !== 'undefined' && window.mobileLog) {
            window.mobileLog(`✅ TAP ACCEPTED: ${el.id}`);
          }
          if (el.onClick) el.onClick();
          if (el.focusable) interactiveRegistry.setFocusByIndex(idx);
          lastTapRef.current = { time: now, id: el.id };
          // Guard follow-up events triggered by state/view changes
        }
        // ── Virtuals: send click action ───────────────────────────────
        sendVirtualsAction({
          type: 'click',
          target: el.id || null,
          x: mx,
          y: my,
          view: osStateRef.current,
          meta: {
            shift: e.shiftKey,
            ctrl: e.ctrlKey,
            alt: e.altKey,
            button: e.button,
            pointerType: e.pointerType || (isMobileEnv ? 'touch' : 'mouse'),
          },
        });
      }
    };

    // CRITICAL FIX: Create the interface object with explicit function reference
    const refInterface = {
      // Always return the offscreen CRT canvas for CRTShader
      getCanvas: () => canvasRef.current,
      getCRTSize: () => ({ width: FRAME_W, height: FRAME_H }),
      getOsState: () => osState,
      // Expose interactive elements for cursor feedback
      getInteractiveElements: () => interactiveRegistry.elements,
      setView: (newView, params = {}) => {
        // Activate guard so the subsequent pointerup/click is swallowed

        // Mark chat visibility based on new view
        if (typeof window !== 'undefined' && window.DCW_setChatVisible) {
          const isChatView = newView === 'chat';
          window.DCW_setChatVisible(isChatView);
          console.log(`[DopeOS] View changed to '${newView}', chat visible: ${isChatView}`);
        }

        setViewState(newView);
        setViewParams(params);
      },
      // Mathematical text rendering API
      setMathematicalText: null, // Will be set by CRTShader
      // --- CRITICAL FIX: handle pointer event forwarded from CRTShader ---
      handlePointerEvent: handlePointerEventFn,
      // --- Canonical: handle keyboard event forwarded from CRTShader ---
      handleKeyEvent: (e) => {
        const state = osStateRef.current;

        if (e.key === 'Escape') {
          if (['boot', 'unbranded', 'asciiTitle', 'loading', 'waitlist', 'waitlistOnboarding'].includes(state)) {
            return;
          }
          const escapeEvent = new CustomEvent('dcw:escape', { cancelable: true, detail: { state } });
          window.dispatchEvent(escapeEvent);
          if (escapeEvent.defaultPrevented) return;
          e.preventDefault();
          if (state === 'chat') {
            setOsState('confirmation');
          } else {
            setOsState('main_menu');
          }
          return;
        }

        const focusables = interactiveRegistry.getFocusableElements();
        if (!focusables.length) return;
        // Tab navigation
        if (e.key === 'Tab') {
          e.preventDefault();
          let idx = interactiveRegistry.focusedIndex;
          idx = (idx + 1) % focusables.length;
          const globalIdx = interactiveRegistry.elements.indexOf(focusables[idx]);
          interactiveRegistry.setFocusByIndex(globalIdx);
          return;
        }
        // Dispatch to focused element
        const focused = interactiveRegistry.getFocused();
        if (focused && focused.onKey) {
          focused.onKey(e);
        } else if (focused && (e.key === 'Enter' || e.key === ' ') && typeof focused.onClick === 'function') {
          e.preventDefault();
          focused.onClick();
        }
        // ── Virtuals: send key action ──────────────────────────────────
        sendVirtualsAction({
          type: 'key',
          key: e.key,
          target: focused?.id || null,
          view: osStateRef.current,
          meta: {
            shift: e.shiftKey,
            ctrl: e.ctrlKey,
            alt: e.altKey,
            button: e.button,
          },
        });
      },
    };

    // CRITICAL DEBUGGING: Verify function exposure
    console.log('[DopeOS] useImperativeHandle: Interface created with keys:', Object.keys(refInterface));
    console.log('[DopeOS] useImperativeHandle: handlePointerEvent type:', typeof refInterface.handlePointerEvent);

    return refInterface;
  }, [FRAME_W, FRAME_H, forcedProfile.id]);

  // ─── Subscribe to Virtuals state_delta stream (Frame SDK) ───────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const v = window.virtuals;
    if (!v || typeof v.onStateDelta !== 'function') return;

    const handler = (msg) => {
      try {
        if (msg?.state_delta && typeof msg.state_delta === 'object') {
          setVirtualsGameState((prev) => ({ ...prev, ...msg.state_delta }));
        }
        if (msg?.view_change && typeof msg.view_change === 'string') {
          setOsState(msg.view_change);
        }
      } catch (err) {
        console.warn('[Virtuals] Failed to process state_delta:', err?.message || err);
      }
    };

    const unsubscribe = v.onStateDelta(handler);
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  // ─── Notify Virtuals host of environment on first mount ───────────────
  useEffect(() => {
    sendVirtualsAction({
      type: 'init',
      resolution: { w: FRAME_W, h: FRAME_H },
      ua: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
      locale: typeof navigator !== 'undefined' ? navigator.language : 'en',
      view: osState,
    });
  }, []);

  useEffect(() => {
    if (!fontReady) return;

    // Smart singleton protection: prevent multiple animation loops with same state
    if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
      const currentLoopState = `${osState}-${forcedProfile.id}`;
      if (window.DCW_ANIMATION_LOOP_RUNNING === currentLoopState) {
        console.warn('[DopeOS]  PHANTOM CODE DETECTED: Animation loop already running for same state - skipping duplicate');
        console.warn(`[DopeOS] Current state: ${currentLoopState}`);
        signingRef.current = false;
        setOsState('waitlist');
        return;
      }
      console.log(`[DopeOS]  Starting animation loop for state: ${currentLoopState} (smart singleton protection)`);
      window.DCW_ANIMATION_LOOP_RUNNING = currentLoopState;
    }

    let cancelled = false;
    // ASCII art fonts are already loaded by DopeTextRenderer.init()
    // No need for additional loading here
    // Always use the offscreen CRT canvas
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error('[DopeOS] Canvas ref is null - cannot initialize renderer');
      return;
    }

    let scaleCanvas = forcedProfile.bufferScale || 1;
    try {
      const isFarcasterDesktop = typeof window !== 'undefined' && window.farcaster && window.parent === window;
      // Allow buffer downscaling if current profile is portrait mobile (9_16)
      if (isFarcasterDesktop && scaleCanvas < 1 && forcedProfile.id !== '9_16') {
        scaleCanvas = 1; // keep full-res only for desktop-style profiles
      }
    } catch (e) {
      console.warn('[DopeOS] Error detecting Farcaster desktop:', e);
      /* SSR */
    }

    CANON_FONT_SIZE = 16;
    CANON_GLOW = 2;

    // Log canvas dimensions for debugging
    console.log(`[DopeOS] Setting canvas dimensions: ${Math.round(FRAME_W * scaleCanvas)}x${Math.round(FRAME_H * scaleCanvas)}, profile: ${forcedProfile.id}, scale: ${scaleCanvas}`);

    canvas.width = Math.round(FRAME_W * scaleCanvas);
    canvas.height = Math.round(FRAME_H * scaleCanvas);

    // Create offscreen buffer (pixel-perfect, low-res)
    const buffer = document.createElement("canvas");
    buffer.width = BUFFER_WIDTH;
    buffer.height = BUFFER_HEIGHT;
    bufferRef.current = buffer;

    console.log(`[DopeOS] Initialized buffer ${BUFFER_WIDTH}x${BUFFER_HEIGHT} for profile ${forcedProfile.id}`);

    // WebGL 2D Context Shim - allows 2D operations on WebGL canvas
    function createWebGL2DContextShim(canvas) {
      try {
        // Check if canvas already has WebGL context
        const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
        if (!gl) return null;

        // Create a proxy object that mimics 2D context API
        const shim = {
          canvas: canvas,
          fillStyle: '#000000',
          strokeStyle: '#000000',
          lineWidth: 1,
          font: '10px monospace',
          textAlign: 'start',
          textBaseline: 'alphabetic',
          globalAlpha: 1.0,
          globalCompositeOperation: 'source-over',

          // Basic drawing methods
          fillRect: function (x, y, w, h) {
            gl.viewport(x, canvas.height - y - h, w, h);
            gl.clearColor(0, 0, 0, 1);
            gl.clear(gl.COLOR_BUFFER_BIT);
          },

          clearRect: function (x, y, w, h) {
            gl.viewport(x, canvas.height - y - h, w, h);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);
          },

          // Text rendering (simplified)
          fillText: function (text, x, y) {
            // For now, just skip text rendering in WebGL shim
            // Could implement WebGL text rendering if needed
            console.log('[WebGL Shim] Text rendering not implemented:', text);
          },

          measureText: function (text) {
            return { width: text.length * 8 }; // Rough estimate
          },

          // Image drawing - CRITICAL FIX: Implement actual image drawing
          drawImage: function (img, ...args) {
            // CRITICAL: If this shim is being used, we need to actually draw the image
            // Create a temporary 2D context to do the actual drawing
            try {
              // Get a REAL 2D context temporarily with willReadFrequently for consistency
              const tempCtx = canvas.getContext('2d', { willReadFrequently: true });
              if (tempCtx && typeof tempCtx.drawImage === 'function') {
                // Use the real 2D context for drawing
                tempCtx.drawImage(img, ...args);
                return;
              }
            } catch (e) {
              console.error('[WebGL Shim] Failed to draw image with real 2D context:', e);
            }
            console.warn('[WebGL Shim] drawImage called but could not execute - canvas will be empty');
          },

          // Save/restore (simplified)
          save: function () { /* no-op */ },
          restore: function () { /* no-op */ },

          // Transform methods (simplified)
          translate: function (x, y) { /* no-op */ },
          scale: function (x, y) { /* no-op */ },
          rotate: function (angle) { /* no-op */ },

          // Path methods (simplified)
          beginPath: function () { /* no-op */ },
          moveTo: function (x, y) { /* no-op */ },
          lineTo: function (x, y) { /* no-op */ },
          closePath: function () { /* no-op */ },
          stroke: function () { /* no-op */ },
          fill: function () { /* no-op */ },

          // Additional properties that might be accessed
          get width() { return canvas.width; },
          get height() { return canvas.height; }
        };

        return shim;
      } catch (e) {
        console.error('[WebGL Shim] Failed to create shim:', e);
        return null;
      }
    }

    // Get contexts with error handling
    // CRITICAL: willReadFrequently: true is essential for performance
    // CRTShader reads these canvases constantly with getImageData() for dirty rect tracking
    let ctx, bctx;
    try {
      ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) {
        // Try WebGL 2D context shim if regular 2D context fails
        ctx = createWebGL2DContextShim(canvas);
        if (!ctx) {
          console.error('[DopeOS] Failed to get 2D context for CRT canvas');
          return;
        }
        console.log('[DopeOS] ⚠️ Using WebGL 2D context shim - this may cause rendering issues');
      } else {
        // Production debug: confirm we're using REAL 2D context
        if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
          console.log('[DopeOS] ✅ Using native 2D context (not shim) with willReadFrequently optimization');
        }
      }
    } catch (e) {
      console.error('[DopeOS] Error getting 2D context for CRT canvas:', e);
      return;
    }

    try {
      bctx = buffer.getContext("2d", { willReadFrequently: true });
      if (!bctx) {
        console.error('[DopeOS] Failed to get 2D context for buffer canvas');
        return;
      }
    } catch (e) {
      console.error('[DopeOS] Error getting 2D context for buffer canvas:', e);
      return;
    }

    let skipCounter = 0;
    function animationLoop() {
      try {
        // During automated hypothesis tests, render every other frame to improve FPS
        if (globalThis.__HYPOTHESIS_TEST) {
          skipCounter = (skipCounter + 1) % 3; // render 1 of 3 frames
          if (skipCounter !== 0) {
            animationRef.current = requestAnimationFrame(animationLoop);
            return;
          }
        }
        interactiveRegistry.clear();
        bctx.fillStyle = '#000';
        bctx.fillRect(0, 0, BUFFER_WIDTH, BUFFER_HEIGHT);
        const time = performance.now() / 1000;

        if (!DopeTextRenderer.isReady()) {
          bctx.fillStyle = '#000';
          bctx.fillRect(0, 0, BUFFER_WIDTH, BUFFER_HEIGHT);
          bctx.font = 'bold 24px monospace';
          bctx.fillStyle = '#39FF14';
          bctx.textAlign = 'center';
          bctx.textBaseline = 'middle';
          bctx.fillText('Loading…', BUFFER_WIDTH / 2, BUFFER_HEIGHT / 2);

          ctx.imageSmoothingEnabled = false;
          ctx.clearRect(0, 0, FRAME_W, FRAME_H);

          if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
            if (!window.DCW_DOPEOS_EARLY_TRANSFER_DEBUG) {
              if (bctx) {
                try {
                  const bufferSample = bctx.getImageData(0, 0, 100, 100);
                  const bufferHasContent = bufferSample.data.some((pixel, idx) => idx % 4 !== 3 && pixel > 5);
                  console.log('[DopeOS] 🔬 EARLY TRANSFER DEBUG (font loading):', {
                    bufferSize: `${buffer.width}x${buffer.height}`,
                    bufferHasContent,
                    reason: 'waiting_for_fonts',
                    bufferRef: !!bufferRef.current,
                    sameBufferInstance: buffer === bufferRef.current
                  });
                  window.DCW_DOPEOS_EARLY_TRANSFER_DEBUG = true;
                } catch (e) {
                  console.warn('[DopeOS] Could not check early buffer content:', e);
                }
              }
            }
          }

          ctx.drawImage(buffer, 0, 0, FRAME_W, FRAME_H);
          animationRef.current = requestAnimationFrame(animationLoop);
          return; // wait for font, skip remainder
        }

        // Use requested view for image generation, otherwise use normal flow
        const activeRequestedView = (typeof window !== 'undefined' && window.__DCW_REQUESTED_VIEW_CLEARED) ? null : requestedView;
        let viewKey;
        if (activeRequestedView) {
          viewKey = isNumericView ? 'error' : activeRequestedView;
          if (viewKey === 'home_strip') viewKey = 'street_home';
        } else if (PROMO_MODE) {
          viewKey = 'promo_logo';
        } else {
          // Determine view based on osState
          if (osState === 'boot') {
            viewKey = 'boot';
          } else if (osState === 'unbranded') {
            viewKey = 'unbranded';
          } else if (osState === 'loading') {
            viewKey = 'loading';
          } else if (osState === 'asciiTitle' || osState === 'waitlist' || osState === 'waitlistOnboarding') {
            viewKey = 'login';
          } else if (osState === 'chat') {
            viewKey = 'comms_window';
          } else if (osState === 'leaderboard') {
            viewKey = 'leaderboard';
          } else if (osState === 'about') {
            viewKey = 'about';
          } else if (osState === 'main_menu') {
            // Street Board retired as home (ADR-0020); street_home stays reachable only via ?view=street_home.
            viewKey = 'dopeos_home';
          } else if (osState === 'street_home') {
            viewKey = 'street_home';
          } else if (osState === 'market') {
            viewKey = 'market';
          } else if (osState === 'route') {
            viewKey = 'route';
          } else if (osState === 'tickets') {
            viewKey = 'tickets';
          } else if (osState === 'comms_window') {
            viewKey = 'comms_window';
          } else if (osState === 'living_chat') {
            viewKey = 'living_chat';
          } else if (osState === 'terminal_window') {
            viewKey = 'terminal_window';
          } else if (osState === 'ledger') {
            viewKey = 'ledger';
          } else if (osState === 'vinnie') {
            viewKey = 'vinnie';
          } else if (osState === 'coat') {
            viewKey = 'coat';
          } else if (osState === 'apps') {
            viewKey = 'apps';
          } else if (osState === 'hronir') {
            viewKey = 'hronir';
          } else if (osState === 'signals') {
            viewKey = 'signals';
          } else if (osState === 'journal') {
            viewKey = 'journal';
          } else if (osState === 'evidence') {
            viewKey = 'evidence';
          } else if (osState === 'story') {
            viewKey = 'story';
          } else if (osState === 'audit') {
            viewKey = 'audit';
          } else if (osState === 'purge') {
            viewKey = 'purge';
          } else if (osState === 'archive') {
            viewKey = 'archive';
          } else {
            viewKey = 'confirmation';
          }
        }

        // Set global current view for scroll handlers
        if (typeof window !== 'undefined') {
          window.DCW_CURRENT_VIEW = viewKey;
        }

        // Debug logging for image generation
        if (isImageGeneration && activeRequestedView) {
          console.log(`DopeOS: Image generation mode - viewKey: ${viewKey}, requestedView: ${activeRequestedView}`);
        }

        const commonParams = {
          onWaitlistClick: handleWaitlistWalletAction,
          onGuestClick: handleGuestLogin,
          authError,
          osState,
          waitlistConfirmed,
          address: userWalletAddress,
          ens: userEnsName,
          gasless: hasSponsoredCalls(),
          isMobile,
          onTalkClick: handleTalkToLloanShark,
          onDisconnectClick: handleDisconnectWallet,
          onAboutClick: handleShowAbout,
          onLeaderboardClick: handleShowLeaderboard,
          onLoginRedirect: () => {
            logger.auth('Redirecting from confirmation to login due to expired session');
            setWaitlistConfirmed(false);
            setUserWalletAddress(null);
            setOsState('waitlist');
          },
          onboardingPending: onboardingPendingRef.current,
        };

        if (osState === 'about') {
          // Provide close handler so About view can exit back to confirmation view
          commonParams.onClose = () => setOsState('confirmation');
        }

        if (osState === 'leaderboard') {
          // Close leaderboard back to confirmation
          commonParams.onClose = () => setOsState('confirmation');
        }

        if (osState === 'chat') {
          commonParams.onCloseChat = () => setOsState('confirmation');
        }

        // Determine params for the current view
        const paramsForView = {
          ...commonParams,
          virtualsState: virtualsGameState,
          isSigning: signingRef.current // Pass signing state to views (especially login)
        };

        // Inject error parameters for numeric views so the generic error renderer
        // receives the correct code and caption.
        if (isNumericView && activeRequestedView) {
          paramsForView.code = activeRequestedView;
          if (!paramsForView.message) {
            paramsForView.message = activeRequestedView.startsWith('4') ? 'PAGE NOT FOUND'
              : activeRequestedView.startsWith('5') ? 'SERVER ERROR'
                : 'ERROR';
          }
        }

        try {
          console.log('[DopeOS] Rendering view:', viewKey, 'osState:', osState);
          renderView(
            bctx,
            time,
            viewKey,
            paramsForView,
            BUFFER_WIDTH,
            BUFFER_HEIGHT,
          );

          // Always verify content for chat/about views
          if (viewKey === 'chat' || viewKey === 'about') {
            try {
              const postRenderSample = bctx.getImageData(0, 0, 100, 100);
              const postRenderHasContent = postRenderSample.data.some((pixel, idx) => idx % 4 !== 3 && pixel > 5);
              console.log('[DopeOS] 🔬 VIEW RENDER VERIFY:', {
                viewKey,
                hasContent: postRenderHasContent,
                bufferSize: `${buffer.width}x${buffer.height}`,
              });
            } catch (e) {
              console.warn('[DopeOS] Could not verify buffer:', e);
            }
          }

        } catch (e) {
          console.error(`[DopeOS] Error rendering view '${viewKey}':`, e);
          // Render fallback error view
          bctx.fillStyle = '#000';
          bctx.fillRect(0, 0, BUFFER_WIDTH, BUFFER_HEIGHT);
          bctx.font = 'bold 24px monospace';
          bctx.fillStyle = '#39FF14';
          bctx.textAlign = 'center';
          bctx.textBaseline = 'middle';
          bctx.fillText('Rendering Error', BUFFER_WIDTH / 2, BUFFER_HEIGHT / 2 - 30);
          bctx.fillText(`View: ${viewKey}`, BUFFER_WIDTH / 2, BUFFER_HEIGHT / 2);
          bctx.fillText('Check console for details', BUFFER_WIDTH / 2, BUFFER_HEIGHT / 2 + 30);
        }

        const focusedElement = interactiveRegistry.getFocused();
        if (focusedElement && focusedElement.w > 0 && focusedElement.h > 0) {
          bctx.save();
          bctx.strokeStyle = '#39FF14';
          bctx.lineWidth = 1;
          bctx.setLineDash([2, 2]);
          bctx.strokeRect(
            Math.round(focusedElement.x) + 1.5,
            Math.round(focusedElement.y) + 1.5,
            Math.max(1, Math.round(focusedElement.w) - 3),
            Math.max(1, Math.round(focusedElement.h) - 3),
          );
          bctx.setLineDash([]);
          bctx.restore();
        }

        // Debug: overlay hit-boxes in red when ?hitbox is present or flag set
        if (SHOW_HITBOXES || (typeof window !== 'undefined' && window.DCW_SHOW_HITBOXES)) {
          drawDebugHitboxes(bctx);
        }
        if (typeof window !== 'undefined' && (window.DCW_SHOW_SAFE || window.localStorage?.getItem('debugSafeArea') === '1')) {
          drawSafeAreaDebug(bctx, { w: BUFFER_WIDTH, h: BUFFER_HEIGHT });
        }

        try {
          ctx.save();
          ctx.imageSmoothingEnabled = false;
          ctx.clearRect(0, 0, FRAME_W, FRAME_H);

          if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
            if (!window.DCW_DOPEOS_TRANSFER_DEBUG) {
              if (bctx) {
                try {
                  const bufferSample = bctx.getImageData(0, 0, 100, 100);
                  const bufferHasContent = bufferSample.data.some((pixel, idx) => idx % 4 !== 3 && pixel > 5);
                  console.log('[DopeOS] 🔬 TRANSFER DEBUG:', {
                    bufferSize: `${buffer.width}x${buffer.height}`,
                    canvasSize: `${canvasRef.current?.width}x${canvasRef.current?.height}`,
                    bufferHasContent,
                    transferTarget: `${FRAME_W}x${FRAME_H}`,
                    ctxValid: !!ctx,
                    bufferRef: !!bufferRef.current,
                    sameBufferInstance: buffer === bufferRef.current
                  });
                  window.DCW_DOPEOS_TRANSFER_DEBUG = true;
                } catch (e) {
                  console.warn('[DopeOS] Could not check buffer content:', e);
                }
              }
            }
          }

          ctx.drawImage(buffer, 0, 0, FRAME_W, FRAME_H);

          if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
            if (!window.DCW_DOPEOS_TRANSFER_VERIFY) {
              try {
                const canvasSample = ctx.getImageData(0, 0, 100, 100);
                const canvasHasContent = canvasSample.data.some((pixel, idx) => idx % 4 !== 3 && pixel > 5);
                console.log('[DopeOS] ✅ TRANSFER VERIFY:', {
                  canvasHasContentAfterTransfer: canvasHasContent,
                  samplePixels: canvasSample.data.length / 4,
                  message: canvasHasContent ? 'SUCCESS - Canvas has content after transfer' : 'FAIL - Canvas still empty after transfer'
                });
                window.DCW_DOPEOS_TRANSFER_VERIFY = true;
              } catch (e) {
                console.warn('[DopeOS] Could not verify transfer:', e);
              }
            }
          }

          ctx.restore();

          // Production debug: detect if canvas is empty/black and content should be visible
          if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
            const shouldHaveContent = osState === 'confirmation' && waitlistConfirmed && userWalletAddress;
            if (shouldHaveContent && time > 5) {
              const imageData = ctx.getImageData(0, 0, Math.min(100, FRAME_W), Math.min(100, FRAME_H));
              const hasContent = imageData.data.some((value, index) => {
                // Check RGB values (skip alpha channel)
                return index % 4 !== 3 && value > 10; // Any color value > 10 indicates content
              });

              if (!hasContent) {
                console.warn('[DopeOS] Production debug: Canvas appears empty despite confirmation state');
                // Force CRT ready event to trigger re-initialization
                if (!window.DCW_CANVAS_DEBUG_TRIGGERED) {
                  window.DCW_CANVAS_DEBUG_TRIGGERED = true;
                  setTimeout(() => {
                    console.log('[DopeOS] Forcing CRT re-initialization...');
                    window.dispatchEvent(new Event('dcw-crt-ready'));
                  }, 1000);
                }
              }
            }
          }
        } catch (e) {
          console.error('[DopeOS] Error drawing buffer to CRT canvas:', e);
        }

        animationRef.current = requestAnimationFrame(animationLoop);
      } catch (err) {
        console.error('[DopeOS] Animation loop error:', err);
        // Try to recover by requesting next frame
        animationRef.current = requestAnimationFrame(animationLoop);
      }
    }

    animationRef.current = requestAnimationFrame(animationLoop);

    // REVERT: Remove signal system - CRT starts immediately for maximum compatibility

    // Add event listener for CRT fallback
    const handleCRTFallback = () => {
      console.log('[DopeOS] CRT fallback triggered - activating direct canvas rendering');
      // Instead of reloading, try to recover the initialization
      if (typeof window !== 'undefined') {
        console.log('[DopeOS] Attempting to recover without reload...');

        // Mark CRT as ready to unblock the UI
        window.DCW_CRT_READY = true;

        // Dispatch ready event to trigger App.jsx handlers
        try {
          window.dispatchEvent(new Event('dcw-crt-ready'));
        } catch (e) {
          console.warn('[DopeOS] Failed to dispatch CRT ready event:', e);
        }

        // Only reload as absolute last resort after 5 seconds
        setTimeout(() => {
          if (!window.DCW_CRT_READY) {
            console.warn('[DopeOS] Recovery failed, reloading page...');
            window.location.reload();
          }
        }, 5000);
      }
    };

    window.addEventListener('dcw-crt-fallback', handleCRTFallback);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('dcw-crt-fallback', handleCRTFallback);

      // Clean up singleton flag for current state
      if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
        const currentLoopState = `${osState}-${forcedProfile.id}`;
        if (window.DCW_ANIMATION_LOOP_RUNNING === currentLoopState) {
          console.log(`[DopeOS] Cleaning up animation loop for state: ${currentLoopState}`);
          window.DCW_ANIMATION_LOOP_RUNNING = null;
        }
      }

      cancelled = true;
    };
  }, [fontReady, osState, forcedProfile.id]); // Include osState to allow view transitions, but singleton protection prevents duplicates

  // Boot sequence → Unbranded logo → asciiTitle → waitlist → blob transitions
  // CRITICAL: Wait for fonts to be ready before starting transitions to ensure visual sync
  useEffect(() => {
    // Don't start boot sequence transitions until fonts are ready for rendering
    if (!fontReady) return;

    if (osState === 'boot') {
      const timeout = setTimeout(() => {
        // Mark boot sequence as seen (cookie) so it won't show again on reload
        setBootSequenceSeen();
        setOsState('unbranded');
      }, BOOT_LINE_DELAY * (BOOT_LINES.length - 1) * 1000);
      return () => clearTimeout(timeout);
    }
    if (osState === 'unbranded') {
      const timeout = setTimeout(() => setOsState('asciiTitle'), UNBRANDED_LOGO_DELAY);
      return () => clearTimeout(timeout);
    }
    if (osState === 'asciiTitle') {
      const timeout = setTimeout(() => setOsState('waitlist'), ASCII_ART_DELAY * 1000);
      return () => clearTimeout(timeout);
    }
    // Waitlist: transition to blob only after wallet action (handled elsewhere)
  }, [osState, fontReady]);

  // --- Canonical: handle buffer click for waitlist button ---

  // Helper to handle successful authentication from any provider
  async function handleAuthSuccess(address) {
    if (!address) return;

    console.log('[DopeOS] Auth success for address:', address);

    // Persist wallet + mark confirmation
    setUserWalletAddress(address);
    setPersistedWallet(address);
    setWaitlistConfirmed(true);

    const defaultCharacterKey = 'vinnie';
    const activeCharacterKey = selectedCharacter || defaultCharacterKey;
    if (!selectedCharacter) {
      setSelectedCharacter(defaultCharacterKey);
    }

    if (!introCompleted) {
      seedChatIntro(activeCharacterKey);
      setIntroCompleted(true);
    }

    // Set grace period to prevent auth polling from interfering immediately after login
    setAuthGracePeriod(true);
    setTimeout(() => {
      setAuthGracePeriod(false);
      console.log('[DopeOS] Auth grace period ended - resuming normal polling');
    }, 10000); // 10 second grace period after successful login

    // Ensure bitmap fonts are fully loaded before showing confirmation UI
    if (!DopeTextRenderer.isReady()) {
      try {
        await DopeTextRenderer.init();
      } catch {/* ignore errors; fallback rendering will handle */ }
    }

    // Ensure plasma glyphs (needed for blob background) are loaded
    if (!plasmaGlyphCache.loaded) {
      try {
        await plasmaGlyphCache.load();
      } catch {/* non-fatal; will fallback to dark bg */ }
    }

    // Transition to blob state (mapped to confirmation view)
    // --- Load initial encounter actions so menu isn't empty ---
    (async () => {
      try {
        const { getFirstEncounter } = await import('../../utils/api');
        const actions = await getFirstEncounter();
        if (Array.isArray(actions) && actions.length) {
          window.dispatchEvent(new CustomEvent('dcw-initial-actions', { detail: actions }));
        }
      } catch (e) { /* non-fatal */ }
    })();
    setOsState('confirmation');

    // Notify rest of app
    const evt = new CustomEvent('dcw-auth-state-change', {
      detail: { state: 'blob', address: address }
    });
    window.dispatchEvent(evt);
  }

  async function handleGuestLogin() {
    const currentState = osStateRef.current;
    if (!['waitlist', 'login', 'asciiTitle', 'waitlistOnboarding'].includes(currentState)) return;
    if (Date.now() - walletActionRef.current < 1500) return;
    walletActionRef.current = Date.now();
    try {
      await ensureCoreSession();
      setWaitlistConfirmed(true);
      setUserWalletAddress(null);
      setUserEnsName(null);
      setOsState('main_menu');
    } catch (error) {
      console.error('[DopeOS] Guest session failed:', error);
      notify(COPY.errors.guest, 'error');
    }
  }

  // Handle wallet action from waitlist screen
  async function handleWaitlistWalletAction() {
    const currentState = osStateRef.current;
    if (!['waitlist', 'login', 'asciiTitle', 'waitlistOnboarding'].includes(currentState)) return;
    if (Date.now() - walletActionRef.current < 1500) return;
    walletActionRef.current = Date.now();
    console.log('[DopeOS] 🖱️ LOGIN CLICKED - Delegating to AuthContext');

    // AuthContext now handles routing to Farcaster/Native/RainbowKit
    // It checks frameBridge.login() first, then triggers RainbowKit if needed.
    if (typeof authLogin === 'function') {
      try {
        await authLogin();
      } catch (err) {
        console.error('[DopeOS] authLogin threw error:', err);
      }
    } else {
      console.error('[DopeOS] authLogin is not a function');
      if (openConnectModal) openConnectModal();
    }

    // Sound effect
    if (typeof safePlaySound === 'function') {
      try { safePlaySound('button_click'); } catch (e) { /* ignore */ }
    }
  }

  // Bootstrap the core guest session independently of the identity provider.
  // Wallet-authenticated users still play on the v0 guest core session.
  React.useEffect(() => {
    ensureCoreSession().catch((error) => {
      console.warn('[DopeOS] Core session bootstrap failed:', error?.message || error);
    });
  }, []);

  // --- Canonical: World IDKit modal logic ---
  const [showWorldIDKit, setShowWorldIDKit] = React.useState(false);
  window.showWorldIDKit = showWorldIDKit;
  function handleWorldProof(result) {
    setShowWorldIDKit(false);
    setIsSigning(false); // Reset signing state on success
    setOsState('confirmation');
  }

  // --- Input handling via CRTShader → handlePointerEvent ---

  // keep UI in sync with account switching
  React.useEffect(() => {
    // Farcaster mini-apps expose an embedded provider whose account list can be
    // empty until the user performs an on-chain action. Treating an empty list
    // as "disconnected" causes a ping-pong back to the waitlist. We therefore
    // ignore account-change events altogether on that platform.
    if (detectPlatform() === PLATFORM.FARCASTER || detectPlatform() === PLATFORM.BASE_MINIKIT || detectPlatform() === PLATFORM.WORLDCOIN) {
      return () => { };
    }

    return addWalletListeners((accounts) => {
      // MetaMask fires accountsChanged twice during signing (first []) then [addr].
      // Ignore the empty array if we are already waiting for signature.
      if (signingRef.current && (!accounts || accounts.length === 0)) return;

      const addr = accounts && accounts.length ? accounts[0] : null;
      if (!addr) {
        // disconnected
        setWaitlistConfirmed(false);
        setUserWalletAddress(null);
        setPersistedWallet('');
        setOsState('waitlist');
      } else if (addr.toLowerCase() !== userWalletAddress?.toLowerCase()) {
        // switched account – require new confirmation
        setWaitlistConfirmed(false);
        setUserWalletAddress(addr);
        setPersistedWallet('');
        setOsState('waitlist');
      }
    });
  }, [userWalletAddress]);

  // --- Transition to chat view ---
  function handleTalkToLloanShark() {
    console.log('[CANVAS CHAT] 🦈 TALK button clicked → entering chat view');
    setOsState('comms_window');

    // subtle haptic feedback when entering chat
    (async () => {
      try {
        if (detectPlatform() === PLATFORM.FARCASTER) return; // Warpcast iOS bug: haptics triggers DM redirect
        const caps = await sdk.getCapabilities?.();
        if (caps?.includes('haptics.selectionChanged')) {
          await sdk.haptics.selectionChanged();
        }
      } catch {/* noop */ }
    })();
  }

  // --- Universal disconnect helper ---
  async function handleDisconnectWallet() {
    console.log('[DopeOS] 🔴 DISCONNECT WALLET - Starting full logout process');

    try {
      // 1. CRITICAL: Call AuthContext logout (which calls authFacade.disconnect())
      console.log('[DopeOS] Calling authLogout() from AuthContext...');
      await authLogout();
      console.log('[DopeOS] ✅ authLogout() completed');

      // 2. Call canonical signOut from utils/auth.js for extra safety
      authSignOut();
      console.log('[DopeOS] ✅ authSignOut() called');

      // 3. Clear API state
      clearAuth();
      invalidateAuth();
      console.log('[DopeOS] ✅ clearAuth() and invalidateAuth() called');

      // 4. Clear localStorage completely
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem('dcw_wallet');
          localStorage.removeItem('dcw_auth_wallet');
          localStorage.removeItem('dcw_api_key');
          localStorage.removeItem('dcw_access_token');
          localStorage.removeItem('dcw_platform_context');
          localStorage.removeItem('dcw_user_identity');
          localStorage.removeItem('dcw_chat_api_key');
          console.log('[DopeOS] ✅ localStorage cleared');
        } catch (error) {
          console.error('[DopeOS] ❌ Failed to clear localStorage:', error.message);
        }
      }

      // 5. Clear local component state
      setPersistedWallet('');
      setWaitlistConfirmed(false);
      setUserWalletAddress(null);
      setUserEnsName(null);
      console.log('[DopeOS] ✅ Component state cleared');

      // 6. Reset chat state to prevent stale data
      resetChatState();
      console.log('[DopeOS] ✅ Chat state reset');

      // 7. Go back to waitlist view
      setOsState('waitlist');
      console.log('[DopeOS] ✅ DISCONNECT COMPLETE - Returned to waitlist');

    } catch (error) {
      console.error('[DopeOS] ❌ Error during disconnect:', error);
      console.error('[DopeOS] ❌ Error stack:', error.stack);
      // Even if there's an error, force back to waitlist
      setOsState('waitlist');
    }
  }

  // --- Navigate to About / Wiki view ---
  function handleShowAbout() {
    if (SHOW_DEBUG_LOGS) logger.debug('[button] ABOUT → about');
    setOsState('about');
  }

  // Farcaster back navigation integration
  React.useEffect(() => {
    if (detectPlatform() !== PLATFORM.FARCASTER) return;

    const setupBackHandler = async () => {
      try {
        // Import SDK dynamically to avoid missing dependencies
        let sdk = null;
        try {
          const sdkModule = await import('@farcaster/miniapp-sdk');
          sdk = sdkModule.sdk || sdkModule.default || sdkModule;
        } catch (err) {
          // SDK not available, check for global injection
          if (typeof window !== 'undefined' && window.farcaster?.sdk) {
            sdk = window.farcaster.sdk;
          } else if (typeof window !== 'undefined' && window.miniKit) {
            sdk = window.miniKit;
          }
        }

        if (sdk && sdk.back) {
          // Show back control
          sdk.back.show?.();

          // Handler uses latest state via ref
          const backHandler = () => {
            if (osState === 'chat') {
              setOsState('confirmation');
              return true;
            }
            if (osState === 'confirmation' && !waitlistConfirmed) {
              setOsState('waitlist');
              return true;
            }
            return false; // let host handle default
          };

          if (typeof sdk.back.onback !== 'undefined') {
            sdk.back.onback = backHandler;
          }
        }
      } catch (e) {
        console.log('Farcaster back handler setup error:', e?.message || e);
      }
    };

    setupBackHandler();
  }, [osState, waitlistConfirmed]);

  // Prevent mounting a new canvas or overlay if hasError
  if (hasError) {
    return (
      <div style={{ width: FRAME_W, height: FRAME_H, background: '#000', color: '#39FF14', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontSize: 24 }}>
        <div>
          <div>DopeOS Error: Failed to load required resources.</div>
          <div>Check your font, bitmap, and network setup.</div>
        </div>
      </div>
    );
  }

  // On mount: if wallet already connected, skip to blob confirmation
  React.useEffect(() => {
    // Skip auth logic when a view is forced via ?view=
    if (requestedView) return;
    // If ?reset is present, allow boot sequence to play before auto-auth skip
    if (FORCE_BOOT) return;
    // Prevent multiple executions and avoid loops
    if (waitlistConfirmed || signingRef.current) return;

    let isCancelled = false;

    async function checkConnectedWallet() {
      // Prevent execution if component unmounted or already confirmed
      if (isCancelled || waitlistConfirmed || signingRef.current) return;

      try {
        // First check if we have a valid session from localStorage
        const apiKey = getApiKey();
        const storedWallet = getAuthWallet();

        // If we have both an API key and a wallet address stored, check if they're valid
        if (apiKey && storedWallet) {
          const authenticated = await isAuthenticated(storedWallet);

          if (authenticated) {
            console.log('[DopeOS] User is already authenticated. Transitioning to blob.');
            setUserWalletAddress(storedWallet);
            setPersistedWallet(storedWallet);

            if (storedWallet) {
              setWaitlistConfirmed(true);
            } else {
              console.log('[DopeOS] 🔧 Cannot confirm waitlist without valid wallet address');
              setWaitlistConfirmed(false);
            }

            // Enhanced readiness check with more comprehensive validation
            const ensureReadyAndTransition = () => {
              const canvasReady = canvasRef.current && canvasRef.current.getContext('2d', { willReadFrequently: true });
              const bufferReady = bufferRef.current && bufferRef.current.getContext('2d', { willReadFrequently: true });
              const rendererReady = DopeTextRenderer.isReady();
              const crtReady = window.DCW_CRT_READY;

              console.log('[DopeOS] Readiness check:', {
                canvasReady: !!canvasReady,
                bufferReady: !!bufferReady,
                rendererReady,
                crtReady,
                animationRef: !!animationRef.current
              });

              if (canvasReady && bufferReady && rendererReady) {
                console.log('[DopeOS] All systems ready, transitioning to confirmation');

                // Ensure animation loop is running
                if (!animationRef.current) {
                  console.log('[DopeOS] Starting animation loop before transition');
                  animationRef.current = requestAnimationFrame(animationLoop);
                }

                setOsState('main_menu');
                notify(COPY.session.returned);
              } else {
                console.log('[DopeOS] Still waiting for systems...');
                setTimeout(ensureReadyAndTransition, 100);
              }
            };

            // Wait longer initial delay for production stability
            setTimeout(ensureReadyAndTransition, 200);
            return;
          } else {
            // Session is invalid - clean up and show login
            console.log('[DopeOS] Stored session is invalid. Cleaning up and showing login.');
            clearAuth();
            setWaitlistConfirmed(false);
          }
        }

        // No auto-login - show waitlist for manual login

        console.log('[DopeOS] No active session or wallet connected. Displaying waitlist.');
        setOsState('waitlist');
      } catch (error) {
        console.error('[DopeOS] Error checking connected wallet on mount:', error);
        setOsState('waitlist');
      }
    }

    checkConnectedWallet();

    return () => {
      isCancelled = true;
    };
  }, []); // Empty dependency array to only run once on mount

  // Function to check session directly from localStorage
  function checkSession() {
    try {
      const apiKey = localStorage.getItem('dcw_api_key') || localStorage.getItem('apiKey');
      const wallet = localStorage.getItem('dcw_auth_wallet') || localStorage.getItem('walletAddress');

      // Only log in development mode to prevent console spam in production
      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        console.log('[DopeOS] Session check: valid session =', !!apiKey && !!wallet);
      }

      return !!apiKey && !!wallet;
    } catch (error) {
      console.error('[DopeOS] Error checking session:', error);
      return false;
    }
  }

  // Shared refreshAuthStatus function used by multiple hooks
  const refreshAuthStatus = useCallback(async (skipScheduleNext = false) => {
    if (requestedView) return;

    // Skip auth check during grace period to avoid race conditions after login
    if (authGracePeriod) {
      console.log('[DopeOS] Skipping auth check during grace period');
      return true;
    }
    try {
      const wallet = getAuthWallet();
      const apiKey = getApiKey();

      if (!wallet || !apiKey) {
        // Reduced logging - no need to log every auth check failure
        setWaitlistConfirmed(false);
        setOsState('waitlist');
        notify(COPY.session.expired);
        return;
      }

      // Reduced logging - auth checks are frequent, no need to log each one

      // First try a direct localStorage check
      const sessionValid = checkSession();

      // Then validate with backend WITH RETRY LOGIC for race conditions
      let stillAuthenticated = false;
      let retryCount = 0;
      const maxRetries = 2;

      while (retryCount <= maxRetries && !stillAuthenticated) {
        try {
          stillAuthenticated = await isAuthenticated(wallet);
          if (!stillAuthenticated && retryCount < maxRetries) {
            console.log(`[DopeOS] Auth check failed, retrying... (${retryCount + 1}/${maxRetries})`);
            // Brief delay before retry to handle backend race conditions
            await new Promise(resolve => setTimeout(resolve, 1000));
            retryCount++;
          } else {
            break;
          }
        } catch (authError) {
          console.log(`[DopeOS] Auth check error on attempt ${retryCount + 1}:`, authError);
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            retryCount++;
          } else {
            throw authError;
          }
        }
      }

      if (!stillAuthenticated) {
        console.log('[DopeOS] Auth validation failed after retries - returning to login');
        setWaitlistConfirmed(false);
        setOsState('waitlist');
        // No need to notify here, as the view change is the notification
      } else {
        // Reduced logging - valid sessions are common, don't spam console

        // Ensure we're in the correct state if authenticated
        // Respect FORCE_BOOT to allow boot sequence to complete
        if (osState === 'waitlist' && !FORCE_BOOT) {
          // Reduced logging - state transitions are common
          setOsState('confirmation');
        }
      }
    } catch (err) {
      console.error('[DopeOS] Auth refresh error; keeping the current session:', err);
    }
  }, [osState, waitlistConfirmed, authGracePeriod]);

  // Auto-refresh authentication status periodically
  useEffect(() => {
    // Only run this effect if the user is authenticated
    if (!waitlistConfirmed) return;

    let isCancelled = false;
    let refreshTimer = null;

    const periodicRefresh = async () => {
      if (isCancelled) return;

      await refreshAuthStatus();

      // Schedule next check if component still mounted
      if (!isCancelled) {
        refreshTimer = setTimeout(periodicRefresh, 30000); // Check every 30 seconds
      }
    };

    // CRITICAL FIX: Defer first refresh to avoid auth checking race conditions
    // Wait 15 seconds after login before first validation check
    const deferTimer = setTimeout(() => {
      if (!isCancelled) {
        periodicRefresh();
      }
    }, 15000); // Wait 15 seconds before first check

    return () => {
      isCancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
      if (deferTimer) clearTimeout(deferTimer);
    };
  }, [waitlistConfirmed, refreshAuthStatus]);

  // PROFESSIONAL AUTH: React to authentication state changes (NO POLLING)
  useEffect(() => {
    // React to authentication changes from AuthContext
    if (authIsAuthenticated && authWallet && getApiKey()) {
      console.log('[DopeOS] Auth state changed - user is authenticated:', authWallet);
      ensureCoreSession().catch((error) => {
        console.warn('[DopeOS] Core session could not start after wallet auth:', error?.message || error);
      });

      // Set wallet address from AuthContext
      setUserWalletAddress(authWallet);
      setPersistedWallet(authWallet);
      setWaitlistConfirmed(true);

      // If ?reset is present, allow boot sequence to complete before transitioning
      const bootSequenceStates = ['boot', 'unbranded', 'asciiTitle', 'waitlist'];
      if (FORCE_BOOT && bootSequenceStates.includes(osState)) return;

      const preEntryStates = ['boot', 'unbranded', 'asciiTitle', 'waitlist', 'waitlistOnboarding', 'loading', 'confirmation'];
      if (preEntryStates.includes(osState)) {
        console.log('[DopeOS] Transitioning to main_menu view from auth, osState was:', osState);
        setOsState('main_menu');

        if (onStateChange) {
          onStateChange('main_menu');
        }

        setViewState('main_menu');
      }
    } else if (!authLoading && !authIsAuthenticated) {
      // User is not authenticated
      console.log('[DopeOS] User not authenticated - clearing state');
      setWaitlistConfirmed(false);
    }
  }, [authIsAuthenticated, authWallet, authLoading, authNativeUser, osState, onStateChange]);

  // Skip boot sequence if wallet is already on waitlist (page reload)
  // Respects ?reset flag to force boot sequence even for authenticated users
  useEffect(() => {
    // If ?reset is present, allow boot sequence to play fully before auto-transitioning
    // Include waitlist to allow the complete boot sequence flow: boot → unbranded → asciiTitle → waitlist
    if (FORCE_BOOT && ['boot', 'unbranded', 'asciiTitle', 'waitlist'].includes(osState)) return;

    const TRANSITIONAL_STATES = ['boot', 'asciiTitle', 'waitlist', 'unbranded', 'confirmation'];

    if (waitlistConfirmed && TRANSITIONAL_STATES.includes(osState)) {
      // Ensure we have a valid wallet address when waitlistConfirmed is true
      if (!userWalletAddress) {
        console.log('[DopeOS] 🔧 State inconsistency detected - waitlistConfirmed without wallet, resetting');
        setWaitlistConfirmed(false);
        setOsState('waitlist');
        return;
      }

      // Reduced logging - auto-transitions are normal behavior
      setOsState('main_menu');
    }
  }, [waitlistConfirmed, osState]);

  profileRef.current = forcedProfile;

  // Allow ?debugSafeArea=1 to auto-enable overlay on mobile where console is harder to reach
  if (typeof window !== 'undefined') {
    if (window.location && (window.location.search.includes('debugSafeArea=1') || window.location.search.includes('safeArea=1'))) {
      try { localStorage.setItem('debugSafeArea', '1'); } catch { /* ignore */ }
    }
  }

  React.useEffect(() => {
    function handleOrientationChange() {
      if (globalThis.__DCW_PORTRAIT_LOCK) return; // Ignore spurious orientation events in Farcaster Desktop
      const newProfile = pickCRTProfile();
      if (newProfile.id !== profileRef.current.id) {
        window.location.reload();
      }
    }
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Listen for navigation events from views
    const handleNavigate = (e) => {
      if (e.detail?.view) {
        window.__DCW_REQUESTED_VIEW_CLEARED = true;
        setOsState(e.detail.view);
      }
    };
    window.addEventListener('dcw:navigate', handleNavigate);
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('dcw:navigate', handleNavigate);
    };
  }, []);

  // Notify parent when osState changes (for DOM overlays)
  React.useEffect(() => {
    onStateChange?.(osState);
  }, [osState, onStateChange]);

  // Keep profile lock flag in sync with state transitions
  React.useEffect(() => {
    if (typeof globalThis === 'undefined') return;
    if (['chat', 'about', 'leaderboard'].includes(osState)) {
      globalThis.__DCW_PROFILE_LOCK = false;
    } else if (['boot', 'login'].includes(osState)) {
      globalThis.__DCW_PROFILE_LOCK = true;
    }
  }, [osState]);

  function handleShowLeaderboard() {
    setOsState('leaderboard');
  }

  // ─── Auto-add Mini-App (Farcaster) – robust retry until SDK ready ───────
  useEffect(() => {
    // Run only on confirmation screen. We no longer rely solely on the
    // detectPlatform() heuristic because some hosts (e.g. Coinbase Wallet
    // running a Farcaster mini-app) may not be detected as FARCASTER even
    // though the Mini-App SDK is injected. Instead, we proceed as soon as
    // the addMiniApp action becomes available.
    if (osState !== 'confirmation') return;

    let cancelled = false;
    async function attemptAdd(retry = 0, delayMs = 250) {
      if (cancelled) return;

      if (SHOW_DEBUG_LOGS) {
        logger.info('[MiniApp] attemptAdd', { retry, delayMs });
      }

      try {
        const installed = localStorage.getItem('dcw_miniapp_installed');
        if (installed === '1') return; // Already added

        // Try multiple SDK patterns to ensure compatibility with all platforms
        // Check for Farcaster SDK (multiple possible locations)
        const farcasterAddFn =
          window?.farcaster?.actions?.addMiniApp ||
          window?.Farcaster?.actions?.addMiniApp ||
          window?.fc?.addMiniApp;

        // Check for generic SDK (our own wrapper)
        const sdkAddFn = sdk?.actions?.addMiniApp;

        // Check for Base SDK (multiple possible locations)
        const baseAddFn =
          window?.Base?.addMiniApp ||
          window?.base?.addMiniApp ||
          window?.baseMiniApp?.add;

        // Check for Worldcoin SDK (multiple possible locations)
        const worldcoinAddFn =
          window?.Worldcoin?.miniApps?.add ||
          window?.worldcoin?.miniApps?.add ||
          window?.worldcoinMiniApp?.add;

        // Use the first available method or fallback
        const addFn = sdkAddFn || farcasterAddFn || baseAddFn || worldcoinAddFn;

        // If no SDK is injected yet, wait and retry – regardless of platform
        if (typeof addFn !== 'function') {
          if (retry < 120 && !cancelled) {
            setTimeout(() => attemptAdd(retry + 1, Math.min(delayMs * 1.5, 2000)), delayMs);
          }
          return;
        }

        await withSuspendedCRT(async () => {
          try {
            // Different SDKs have different calling conventions
            let result;
            const appUrl = window.location.href;
            const appName = 'Dope Chain Wars';

            if (sdkAddFn) {
              // Our wrapper SDK
              result = await sdkAddFn();
            } else if (farcasterAddFn) {
              // Farcaster may accept different params
              try {
                result = await farcasterAddFn({ url: appUrl, name: appName });
              } catch (e) {
                // Try alternative signature
                result = await farcasterAddFn(appUrl, appName);
              }
            } else if (baseAddFn) {
              // Base may accept different params
              try {
                result = await baseAddFn({ url: appUrl, name: appName });
              } catch (e) {
                // Try alternative signature
                result = await baseAddFn(appUrl, appName);
              }
            } else if (worldcoinAddFn) {
              // Worldcoin may accept different params
              try {
                result = await worldcoinAddFn({ url: appUrl, name: appName });
              } catch (e) {
                // Try alternative signature
                result = await worldcoinAddFn(appUrl, appName);
              }
            }

            logger.info('[MiniApp] addMiniApp succeeded with platform',
              sdkAddFn ? 'generic' :
                farcasterAddFn ? 'farcaster' :
                  baseAddFn ? 'base' :
                    worldcoinAddFn ? 'worldcoin' : 'unknown');

            // Now that the mini-app is installed we can transition to the blob view
            if (osStateRef.current === 'confirmation') {
              setOsState('confirmation');
            }
          } catch (innerErr) {
            logger.warn('[MiniApp] Inner execution failed:', innerErr?.message || innerErr);
            // Don't rethrow - we'll still mark as "successful" to avoid endless retries
          }
        });

        if (!cancelled) {
          localStorage.setItem('dcw_miniapp_installed', '1');
        }
      } catch (err) {
        logger.warn('[MiniApp] addMiniApp failed:', err?.message || err);
        // Retry up to 10 times with backoff in case of transient failure
        if (retry < 10 && !cancelled) {
          setTimeout(() => attemptAdd(retry + 1, Math.min(delayMs * 2, 4000)), delayMs);
        }
      }
    }

    attemptAdd();
    return () => { cancelled = true; };
  }, [osState]);

  // Base App workaround: if WebView returns from background with hidden CRT
  // we perform a one-time reload on visibilitychange.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        // Safe access to miniKit to avoid cross-origin SecurityError
        const miniKit = window.miniKit;
        if (miniKit && !miniKit.isFrameContext) {
          const visFlag = 'dcw_after_visibility_reload';
          const handler = () => {
            if (!document.hidden && !sessionStorage.getItem(visFlag)) {
              console.log('[DopeOS] Base App workaround triggered - reloading page');
              sessionStorage.setItem(visFlag, '1');
              window.location.reload();
            }
          };
          document.addEventListener('visibilitychange', handler, { once: true });

          return () => {
            document.removeEventListener('visibilitychange', handler);
          };
        }
      } catch (error) {
        // Silently ignore cross-origin access errors
        if (!(error instanceof DOMException && error.name === 'SecurityError')) {
          console.warn('[DopeOS] Error in Base App workaround:', error);
        }
      }
    }
  }, []); // Only run once on mount

  // Authentication state logging moved to mount effect to avoid frame-by-frame spam

  // DEBUG: Log authentication state once on mount for troubleshooting
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
      console.log(' DEBUG AUTH STATE (mount):');
      const apiKey = localStorage.getItem('dcw_api_key');
      const wallet = localStorage.getItem('dcw_wallet');
      console.log('API Key exists:', !!apiKey);
      console.log('Wallet exists:', !!wallet);
      if (wallet) console.log('Wallet (first 10):', wallet.substring(0, 10) + '...');
    }
  }, []); // Run only once on mount

  // Keyboard shortcut: "L" → Leaderboard (desktop only)
  useEffect(() => {
    const keyHandler = (e) => {
      const el = e.target;
      const isTypingElement =
        el && (
          el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.isContentEditable
        );

      // Skip if user is typing or the chat view is active
      if (
        isTypingElement ||
        osStateRef.current === 'chat' ||
        window.DCW_CURRENT_VIEW === 'comms_window' ||
        window.DCW_CURRENT_VIEW === 'living_chat'
      ) return;

      if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        setOsState('leaderboard');
      }
    };
    window.addEventListener('keydown', keyHandler);
    return () => window.removeEventListener('keydown', keyHandler);
  }, []);

  // ─── Active Profile Watchdog ─────────────────────────────────────────────
  // Continuously monitor for environment mismatches (e.g. Desktop profile active
  // while inside a mobile app webview). This fixes "Tiny Text" and layout issues
  // caused by race conditions during initial load.
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

      const ua = navigator.userAgent || '';
      // Comprehensive mobile/app detection regex
      const isAppEnv = /Base|Minikit|Farcaster|World App|WorldCoin|iPhone|iPad|Android/i.test(ua);

      // If we are definitely in a mobile/app environment
      if (isAppEnv) {
        // Check if we are using a desktop profile (buffer width > 500)
        // Note: profileRef.current is updated on every render
        const currentProfile = profileRef.current;
        if (currentProfile && currentProfile.buffer && currentProfile.buffer.w > 500) {
          // Double check we aren't just on a very large tablet that *wants* desktop mode
          // But for "Base", "Farcaster", "World App", we ALWAYS want mobile profile regardless of screen size
          const isEmbeddedWallet = /Base|Minikit|Farcaster|World App|WorldCoin/i.test(ua);

          if (isEmbeddedWallet || window.innerWidth < 768) {
            if (!window.DCW_WATCHDOG_WARNING_LOGGED) {
              console.warn('[Watchdog] 🚨 Environment mismatch detected! Mobile/App environment using Desktop profile.');
              console.warn(`[Watchdog] UA: ${ua}`);
              console.warn(`[Watchdog] Profile: ${currentProfile.id} (${currentProfile.buffer.w}x${currentProfile.buffer.h})`);
              window.DCW_WATCHDOG_WARNING_LOGGED = true;
            }

            // Force reset logic
            if (!window.DCW_WATCHDOG_RESET_TRIGGERED) {
              window.DCW_WATCHDOG_RESET_TRIGGERED = true;
              console.log('[Watchdog] 🔄 Forcing profile reset and reload...');

              // Clear session storage locks
              try {
                sessionStorage.removeItem('dcw_last_viewport');
                sessionStorage.removeItem('dcw_mobile_detection_temp');
                if (typeof globalThis !== 'undefined') globalThis.__DCW_PROFILE_LOCK = false;
              } catch (e) { }

              // Reload to force fresh detection and profile selection
              window.location.reload();
            }
          }
        }
      }
    }, 1000); // Check every 1 second

    return () => clearInterval(intervalId);
  }, []);

  // Handle view transitions
  window.DCW_showView = (viewName) => {
    console.log(`[DopeOS] Transitioning to view: ${viewName}`);
    setOsState(viewName);
  };

  // Expose current view state for chat scroll handling
  window.DCW_getCurrentView = () => view;


  // ─── DEBUG OVERLAY ───────────────────────────────────────────────────────
  // Render a visible debug overlay if we are in a "Nuclear" override state
  // or if explicitly enabled. This helps diagnose why regexes fail on real devices.
  const [showDebugOverlay, setShowDebugOverlay] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // ❌ NEVER show debug overlay on mobile devices (too intrusive for users)
      // Only show on desktop with explicit ?debug=true URL param
      const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
      const hasDebugParam = window.location.search.includes('debug');

      if (!isMobile && hasDebugParam) {
        setShowDebugOverlay(true);
      }
    }
  }, []);

  const IS_FARCASTER = detectPlatform() === PLATFORM.FARCASTER;
  if (IS_FARCASTER && typeof window !== 'undefined') {
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('dcw-boot-ready'));
    });
  }

  // CRITICAL FIX: Remove IDKitWidget for Farcaster entirely to prevent black screen
  // Farcaster uses its own auth, so IDKit is not needed unless user specifically requests World ID
  // but since they are mutually exclusive platforms, we can safe-guard this.
  // 
  // ALSO: If screen is small (< 500), assume we are on mobile (including Farcaster mobile),
  // where IDKit might cause rendering conflicts if initialized too early.
  // 
  // Finally, check global overrides directly.
  const globalPlatform = typeof window !== 'undefined' ? window.DCW_PLATFORM_OVERRIDE : null;
  const isGlobalFarcaster = globalPlatform === 'farcaster';
  const isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 500;

  const canRenderIDKit = detectPlatform() !== PLATFORM.FARCASTER && !isGlobalFarcaster;

  return (
    <>
      {/* DEBUG OVERLAY */}
      {showDebugOverlay && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 10000,
          background: 'rgba(0,0,0,0.8)',
          color: '#0f0',
          fontFamily: 'monospace',
          fontSize: '10px',
          padding: '4px',
          pointerEvents: 'none',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all'
        }}>
          <div>UA: {typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'}</div>
          <div>Override: {typeof window !== 'undefined' ? window.DCW_PROFILE_OVERRIDE || 'None' : 'N/A'}</div>
          <div>Platform: {typeof window !== 'undefined' ? window.DCW_PLATFORM_OVERRIDE || 'None' : 'N/A'}</div>
          <div>Dims: {typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'N/A'}</div>
        </div>
      )}

      {/* World ID verification now handled via @worldcoin/minikit-js in frameBridge.js */}
      {showWorldIDKit && canRenderIDKit && (
        <div style={{ position: 'absolute', zIndex: 9999, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <div style={{ 
            padding: '20px', 
            background: '#000', 
            border: '2px solid #0f0', 
            color: '#0f0', 
            fontFamily: 'monospace',
            textAlign: 'center'
          }}>
            World ID verification initiated via MiniKit...
          </div>
        </div>
      )}
      <div
        ref={mountRef}
        className="dcw-os-shell"
        role="application"
        aria-label="Dope Chain Wars CRT terminal"
        tabIndex={0}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          backgroundColor: 'black',
          ...style
        }}
      >
        <p className="dcw-sr-only" aria-live="polite">
          Dope Chain Wars terminal. Use Tab to move focus, Enter to select, and Escape to return to the desktop.
        </p>
      </div>
    </>
  );
});

// Export the DopeOS component as default
export default DopeOS;

// Export a wrapper function for sendVirtualsAction that will use the internal function
export function sendVirtualsAction(action) {
  // This is a wrapper that will be used by external code
  // The actual implementation is inside the DopeOS component
  const v = globalThis.virtuals ?? (typeof window !== 'undefined' ? window.virtuals : undefined) ?? {};
  const payload = { ...action, ts: Date.now() };
  try {
    v.requestTurn?.([payload]);
  } catch (err) {
    console.warn('[Virtuals] requestTurn failed:', err);
  }
} 